// RetailSalesPage — Hızlı Perakende Satış (POS) — Tam ekran touch-first tasarım
// GK Software OmniPOS / Toast / Square tarzı.
// Market, tekstil mağaza, manav, kafe gibi perakende sektörlerinin tümünü destekler.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Hesaplama yardımcıları ────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(n) || 0);

// Para birimi sembolü — kod -> sembol
const CUR_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€', GBP: '£', JPY: '¥', CHF: 'CHF', RUB: '₽', CNY: '¥' };
const curSym = (code) => CUR_SYMBOLS[String(code || 'TRY').toUpperCase()] || code || '₺';

// Favori buton renkleri — GK tarzı renkli grid
const FAV_COLORS = [
  '#3b82f6', '#06b6d4', '#10b981', '#84cc16',
  '#eab308', '#f59e0b', '#f97316', '#ef4444',
  '#ec4899', '#a855f7', '#8b5cf6', '#6366f1',
];

// ── 80mm Bilgi Fişi — terminal yazıcısına HTML print penceresi ──────────
function printInfoSlip({ invoiceNo, invoiceDate, customer, customerVkn, taxpayer, lines, totals, splits }) {
  const w = window.open('', '_blank', 'width=400,height=700');
  if (!w) { alert('Yazdırma penceresi açılamadı. Tarayıcı popup engelini kontrol edin.'); return; }
  const dt = invoiceDate ? new Date(invoiceDate) : new Date();
  const dateStr = dt.toLocaleDateString('tr-TR') + ' ' + dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  const fmtTL = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(n) || 0);
  const rows = (lines || []).map(l => {
    const qty  = parseFloat(l.quantity) || 0;
    const unit = parseFloat(l.unit_price) || 0;
    const vat  = parseFloat(l.vat_rate) || 0;
    const tot  = qty * unit * (1 + vat / 100);
    return `
      <tr><td colspan="3" style="padding-top:4px;font-weight:600">${(l.name || l.stock_name || '').substring(0, 40)}</td></tr>
      <tr>
        <td style="font-size:10px;color:#444">${qty} x ${fmtTL(unit)}</td>
        <td style="font-size:10px;text-align:right;color:#444">KDV %${vat}</td>
        <td style="text-align:right;font-weight:600">${fmtTL(tot)}</td>
      </tr>`;
  }).join('');

  const payRows = [];
  if ((splits.cash || 0) > 0.01)    payRows.push(['NAKİT',         splits.cash]);
  (splits.cards     || []).forEach(c => c.amount > 0.01 && payRows.push(['KREDİ KARTI', c.amount]));
  (splits.transfers || []).forEach(c => c.amount > 0.01 && payRows.push(['HAVALE/EFT',  c.amount]));
  (splits.vouchers  || []).forEach(c => c.amount > 0.01 && payRows.push(['HEDİYE ÇEKİ', c.amount]));
  if ((splits.on_account || 0) > 0.01) payRows.push(['CARİYE',     splits.on_account]);
  const payHtml = payRows.map(([k, v]) => `<tr><td>${k}</td><td style="text-align:right">${fmtTL(v)}</td></tr>`).join('');

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Bilgi Fişi ${invoiceNo || ''}</title>
<style>
  @media print { @page { size: 80mm auto; margin: 4mm; } body { margin: 0; } .no-print { display: none !important; } }
  body { font-family: 'Courier New', monospace; font-size: 12px; width: 72mm; margin: 0 auto; padding: 8px; color: #000; }
  h1 { text-align: center; font-size: 14px; margin: 0 0 4px; }
  .center { text-align: center; }
  .meta  { font-size: 11px; line-height: 1.4; margin: 6px 0; }
  hr { border: none; border-top: 1px dashed #888; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  td { padding: 1px 0; vertical-align: top; }
  .total { font-weight: bold; font-size: 13px; border-top: 1px solid #000; padding-top: 4px; }
  .small { font-size: 10px; color: #444; }
  .no-print { padding: 12px; text-align: center; background: #f0f0f0; }
  .no-print button { padding: 10px 24px; font-size: 14px; cursor: pointer; background: #10b981; color: #fff; border: none; border-radius: 6px; font-weight: 700; }
</style></head>
<body>
  <div class="no-print">
    <button onclick="window.print()">🖨️ Yazdır</button>
    <button onclick="window.close()" style="background:#888;margin-left:8px">Kapat</button>
  </div>
  <h1>${taxpayer?.legal_name || 'BİLGİ FİŞİ'}</h1>
  <div class="center small">VKN: ${taxpayer?.tax_number || '—'} ${taxpayer?.tax_office ? '/ ' + taxpayer.tax_office : ''}</div>
  <hr>
  <div class="meta">
    <div><strong>Fiş No:</strong> ${invoiceNo || '—'}</div>
    <div><strong>Tarih:</strong> ${dateStr}</div>
    <div><strong>Müşteri:</strong> ${customer}</div>
    ${customerVkn ? `<div><strong>VKN/TCKN:</strong> ${customerVkn}</div>` : ''}
  </div>
  <hr>
  <table>${rows}</table>
  <hr>
  <table>
    <tr><td>Ara Toplam</td><td style="text-align:right">${fmtTL(totals.subtotal || totals.total || 0)}</td></tr>
    ${totals.vatTotal ? `<tr><td>Toplam KDV</td><td style="text-align:right">${fmtTL(totals.vatTotal)}</td></tr>` : ''}
    <tr class="total"><td>GENEL TOPLAM</td><td style="text-align:right">${fmtTL(totals.grandTotal)} TL</td></tr>
  </table>
  <hr>
  <div class="small" style="text-align:center;margin-bottom:4px">ÖDEME</div>
  <table>${payHtml}</table>
  <hr>
  <div class="center small">
    <strong>BU BİR MALİ BELGE DEĞİLDİR</strong><br>
    Sadece bilgi amaçlıdır.
  </div>
  <hr>
  <div class="center small">Teşekkür ederiz.</div>
  <script>
    window.onload = function() { setTimeout(function() { window.print(); }, 400); };
  </script>
</body></html>`;
  w.document.write(html);
  w.document.close();
}

// ── Ana sayfa ─────────────────────────────────────────────────────────
export default function RetailSalesPage() {
  const { user } = useAuth();

  const [lines, setLines] = useState([]);           // sepet satırları
  const [activeLineIdx, setActiveLineIdx] = useState(null);  // seçili satır (sarı vurgulu)
  // 'barcode' (default — rakamlar barkod inputuna gider) | 'quantity' | 'price' | 'discount' (buffer'a gider)
  const [editMode, setEditMode] = useState('barcode');
  const [numericBuffer, setNumericBuffer] = useState('');    // numerik tuş tamponu
  const [activeTab, setActiveTab] = useState('actions');     // 'actions' (İşlemler) | 'favorites' | 'operational'
  const [activeCategory, setActiveCategory] = useState('all'); // 'all' veya kategori id

  const [stocks, setStocks] = useState([]);          // arama için stok listesi
  const [favorites, setFavorites] = useState([]);    // favori ürünler (en sık satılan ilk 24)
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [cashRegisters, setCashRegisters] = useState([]);
  const [cashRegisterId, setCashRegisterId] = useState('');
  // YN ÖKC — yazar kasa entegrasyonu
  const [posTerminals, setPosTerminals] = useState([]);
  const [activeTerminalId, setActiveTerminalId] = useState('');
  const [posOverlay, setPosOverlay] = useState(null); // null | { stage, text } — Protel akışı sırasında ekran
  const [showOkcChecks, setShowOkcChecks] = useState(false);
  const [okcChecks, setOkcChecks] = useState({ loading: false, items: [], err: '' });
  // Bilgi fişi onay modal — kasa yok + e-fatura ayarı yok senaryosu
  const [infoSlipPrompt, setInfoSlipPrompt] = useState(null); // null | { splits, missing, taxpayer }
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [posDefinitions, setPosDefinitions] = useState([]);   // aktif POS tanımları (kredi kartı için)
  const [availableVouchers, setAvailableVouchers] = useState([]); // seçili müşterinin issued voucher'ları
  const [branches, setBranches] = useState([]);                // aktif şubeler — üst bar + yeni müşteri formu için
  const [appSettings, setAppSettings] = useState({});
  const [exchangeRates, setExchangeRates] = useState({});

  // Perakende fiyat tanımı — stocks.last_sale_price yerine stock_price_items.price1 okur
  const [priceDef, setPriceDef] = useState(null);    // { id, name, currency_code, ... }

  const [barcodeInput, setBarcodeInput] = useState('');
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showCustomerPick, setShowCustomerPick] = useState(false);
  const [weighedPick, setWeighedPick] = useState(null);  // tartılı favori için miktar soran modal
  const [hoverImg, setHoverImg] = useState(null);        // { url, x, y, name } — büyük önizleme
  const [parkedSales, setParkedSales] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pos_parked_sales') || '[]'); } catch { return []; }
  });
  const [showParked, setShowParked] = useState(false);
  const [showStockStatus, setShowStockStatus] = useState(false);
  // İskonto/Yuvarlama header state — kaydederken her satıra dağıtılır
  const [headerDisc, setHeaderDisc] = useState({
    d1: { type: 'percent', value: 0 },  // 1. iskonto: 'percent' veya 'amount'
    d2: { type: 'percent', value: 0 },
    d3: { type: 'percent', value: 0 },
    rounding: { type: 'amount', value: 0 },  // yuvarlama
  });
  const [showDiscount, setShowDiscount] = useState(false);
  const [showRounding, setShowRounding] = useState(false);
  const [showLineDiscount, setShowLineDiscount] = useState(false);
  const [priceTier, setPriceTier] = useState(1);          // 1=peşin (price1), 3=vadeli (price3)
  const [showTierChangeConfirm, setShowTierChangeConfirm] = useState(null);  // { from, to }
  const [customer, setCustomer] = useState(null);    // null = ayardan henüz okunmadı; default değilse manuel seçilmiş
  const [defaultCustomer, setDefaultCustomer] = useState(null); // settings.retail_default_account_id'den
  const [personnel, setPersonnel] = useState([]);    // tüm aktif personel listesi
  const [activePersonnel, setActivePersonnel] = useState(null);  // satıra atanacak personel
  const [showPersonnelPick, setShowPersonnelPick] = useState(false);
  // null = üst bar default değiştir; sayı = o satırın personelini değiştir
  const [personnelPickTarget, setPersonnelPickTarget] = useState(null);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  // Şarj tarihi
  const now = new Date();
  const dateStr = now.toLocaleDateString('tr-TR');
  const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

  const barcodeRef = useRef(null);

  // ── İlk yükleme ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [wRes, cRes, sRes, pRes, bRes, posRes, brRes, ptRes] = await Promise.all([
          api.get('/warehouses'),
          api.get('/cash-registers').catch(() => ({ data: { success: false } })),
          api.get('/settings').catch(() => ({ data: { success: false } })),
          api.get('/personnel').catch(() => ({ data: { success: false } })),
          api.get('/bank-accounts').catch(() => ({ data: { success: false } })),
          api.get('/bank-pos?is_active=yes').catch(() => ({ data: { success: false } })),
          api.get('/branches?is_active=yes').catch(() => ({ data: { success: false } })),
          api.get('/pos-terminals?is_active=yes').catch(() => ({ data: { success: false } })),
        ]);
        const whs = wRes.data?.success ? (wRes.data.data?.items || wRes.data.data || []) : [];
        const crs = cRes.data?.success ? (cRes.data.data?.items || cRes.data.data || []) : [];
        const stg = sRes.data?.success ? (sRes.data.data || {}) : {};
        const pers = pRes.data?.success ? (pRes.data.data?.items || pRes.data.data || []) : [];
        const banks = bRes.data?.success ? (bRes.data.data?.items || bRes.data.data || []) : [];
        const poss = posRes.data?.success ? (posRes.data.data?.items || posRes.data.data || []) : [];
        const brs  = brRes.data?.success ? (brRes.data.data?.items || []) : [];
        setWarehouses(whs);
        setCashRegisters(crs);
        setAppSettings(stg);
        setPersonnel(pers);
        setBankAccounts(banks);
        setBranches(brs);
        setPosDefinitions(poss);

        // Aktif şubeye bağlı YN ÖKC kasaları
        const userBranch = user?.defaultBranchId || user?.default_branch_id;
        const allPts = ptRes.data?.success ? (ptRes.data.data?.items || []) : [];
        const branchPts = userBranch ? allPts.filter(t => t.branch_id === userBranch) : allPts;
        setPosTerminals(branchPts);
        if (branchPts.length > 0) setActiveTerminalId(branchPts[0].id);

        // Öncelik: kullanıcı kartındaki → ayarlardaki retail_default → genel default
        const userWh = user?.defaultWarehouseId || user?.default_warehouse_id;
        const userCr = user?.defaultCashRegisterId || user?.default_cash_register_id;
        const resolvedWh = userWh || stg.retail_default_warehouse_id || stg.default_warehouse_id || '';
        const resolvedCr = userCr || stg.retail_default_cash_register_id || stg.default_cash_register_id || '';
        setWarehouseId(resolvedWh);
        setCashRegisterId(resolvedCr);
        if (banks.length) setBankAccountId(banks[0].id);

        // Varsayılan cari — settings.retail_default_account_id'den al
        if (stg.retail_default_account_id) {
          try {
            const a = await api.get('/accounts/' + stg.retail_default_account_id);
            if (a.data?.success) setDefaultCustomer(a.data.data);
          } catch {}
        }

        // Varsayılan personel — önce kullanıcı kartındaki personnelId (kasiyer kendisi), sonra settings
        const userPersonnelId = user?.personnelId || user?.personnel_id;
        if (userPersonnelId) {
          const me = pers.find(x => x.id === userPersonnelId);
          if (me) setActivePersonnel(me);
        }
        if (!userPersonnelId && stg.retail_default_personnel_id) {
          const p = pers.find(x => x.id === stg.retail_default_personnel_id);
          if (p) setActivePersonnel(p);
        }
      } catch (e) { console.error(e); }
    })();

    // Perakende fiyat tanımını bul (price_group='perakende') — önce tercih edilen, sonra ilki
    (async () => {
      try {
        const r = await api.get('/price-definitions?price_group=perakende&is_active=yes');
        if (r.data.success) {
          const list = r.data.data || [];
          const preferred = list.find(p => p.is_preferred) || list[0] || null;
          setPriceDef(preferred);
          // Favori ürünleri bu fiyat tanımının price1 değerleriyle yükle
          if (preferred) {
            const lkp = await api.get('/sales-invoices/lookups/all?retail_only=true&price_definition_id=' + preferred.id);
            if (lkp.data?.data?.stocks) {
              // SADECE is_favorite=1 işaretli stoklar
              const favOnly = lkp.data.data.stocks.filter(s => s.is_favorite);
              setFavorites(favOnly);
            }
            if (lkp.data?.data?.exchangeRates) {
              setExchangeRates(lkp.data.data.exchangeRates);
            }
          }
        }
      } catch (e) {
        console.error('priceDef yüklenemedi:', e);
      }
    })();

    // Barkod inputuna otomatik focus
    setTimeout(() => barcodeRef.current?.focus(), 100);
  }, []); // eslint-disable-line

  // Müşteri değişince o müşterinin elindeki (issued) alış-veriş çeklerini çek
  useEffect(() => {
    const cid = customer?.id || defaultCustomer?.id;
    if (!cid) { setAvailableVouchers([]); return; }
    api.get('/shopping-vouchers/available/' + cid)
      .then(r => setAvailableVouchers(r.data?.data || []))
      .catch(() => setAvailableVouchers([]));
  }, [customer?.id, defaultCustomer?.id]);

  // Renk/Beden takibi açık mı? (ayarlardan)
  const colorSizeEnabled = (() => {
    const v = appSettings?.color_size_tracking;
    if (v === true || v === 1) return true;
    return String(v || '').toLowerCase() === 'true';
  })();
  const discountLevels = (() => {
    const raw = appSettings?.retail_discount_levels;
    if (raw == null || raw === '') return 1; // SettingsPage varsayılanı ile aynı
    return Math.max(0, Math.min(3, parseInt(raw) || 0));
  })();
  const roundingEnabled = String(appSettings?.retail_rounding_enabled || 'true').toLowerCase() === 'true';

  // ── Hesaplamalar ────────────────────────────────────────────────────
  // İskonto/Yuvarlama header'a uygulanır, gross toplam üstünden hesaplanır
  const totals = (() => {
    let subtotalRaw = 0, vatRaw = 0, gross = 0, afterLineDisc = 0, lineDiscTotal = 0;
    let netAfterLine = 0, vatAfterLine = 0;
    lines.forEach((l) => {
      const qty = parseFloat(l.quantity) || 0;
      const price = parseFloat(l.unit_price) || 0;
      const vatRate = parseFloat(l.vat_rate) || 0;
      const lineNet = qty * price;
      const lineVat = lineNet * (vatRate / 100);
      const lineGross = lineNet + lineVat;
      const ld1 = parseFloat(l.discount_rate1) || 0;
      const ld2 = parseFloat(l.discount_rate2) || 0;
      const ld3 = parseFloat(l.discount_rate3) || 0;
      const factor = (1 - ld1 / 100) * (1 - ld2 / 100) * (1 - ld3 / 100);
      const lineNetAfter = lineNet * factor;
      const lineVatAfter = lineVat * factor;
      const afterLine = lineGross * factor;
      subtotalRaw += lineNet;
      vatRaw += lineVat;
      gross += lineGross;
      netAfterLine += lineNetAfter;
      vatAfterLine += lineVatAfter;
      afterLineDisc += afterLine;
      lineDiscTotal += (lineGross - afterLine);
    });

    // Header iskontoları (satır iskontolarından sonra kalan üstüne uygulanır)
    let after = afterLineDisc;
    const calcStep = (d) => {
      if (!d || !d.value) return 0;
      if (d.type === 'percent') return after * (parseFloat(d.value) || 0) / 100;
      return parseFloat(d.value) || 0;
    };
    const disc1 = discountLevels >= 1 ? calcStep(headerDisc.d1) : 0; after -= disc1;
    const disc2 = discountLevels >= 2 ? calcStep(headerDisc.d2) : 0; after -= disc2;
    const disc3 = discountLevels >= 3 ? calcStep(headerDisc.d3) : 0; after -= disc3;
    // Yuvarlama (disc5): tutar veya % (gross üstünden negatif/pozitif)
    let rnd = 0;
    if (roundingEnabled && headerDisc.rounding?.value) {
      if (headerDisc.rounding.type === 'percent') rnd = after * (parseFloat(headerDisc.rounding.value) || 0) / 100;
      else rnd = parseFloat(headerDisc.rounding.value) || 0;
    }
    after -= rnd;

    // Final NET ve KDV — iskonto + yuvarlama sonrası oransal olarak çıkarılır
    const finalRatio = afterLineDisc > 0 ? (after / afterLineDisc) : 0;
    const subtotal = netAfterLine * finalRatio;
    const vat      = vatAfterLine * finalRatio;

    return { subtotal, vat, total: gross, lineDiscTotal, disc1, disc2, disc3, rnd, grandTotal: after,
             subtotalRaw, vatRaw };
  })();

  // ── Sepet işlemleri ─────────────────────────────────────────────────
  // Bir stoktan satış fiyatı al — priceTier'a göre price1/price2/price3
  // Tier'a karşılık fiyat yoksa price1'e fallback
  const resolvePrice = (stock, tier = priceTier) => {
    const key = 'pd_price' + tier;
    const v = stock[key];
    if (v != null && parseFloat(v) > 0) return parseFloat(v);
    if (stock.pd_price1 != null) return parseFloat(stock.pd_price1) || 0;
    return 0;
  };

  const addLine = (stock, qty = 1, priceOverride = null) => {
    const unitPrice = priceOverride != null ? priceOverride : resolvePrice(stock);
    const vatRate = parseFloat(stock.vat_rate) || 20;
    const vatIncluded = !!stock.pd_vat_included;
    // Yabancı döviz mi? Yerel paraya çevir (selling_rate)
    const localCurrency = appSettings?.local_currency || priceDef?.currency_code || 'TRY';
    const stockCurrency = (stock.pd_currency_code || priceDef?.currency_code || localCurrency).toUpperCase();
    const isForeign = stockCurrency !== localCurrency.toUpperCase();
    const exchangeRate = isForeign
      ? parseFloat(exchangeRates?.[stockCurrency]?.selling_rate || exchangeRates?.[stockCurrency]?.mid_rate) || 1
      : 1;
    // unitPrice = girilen fiyat (KDV dahil olabilir)
    // Yabancı net fiyat (kendi dövizinde KDV hariç)
    const foreignGross = unitPrice;
    const foreignNet = vatIncluded && foreignGross > 0
      ? +(foreignGross / (1 + vatRate / 100)).toFixed(4)
      : foreignGross;
    // Yerel para — net üstünden hesaplanır (KDV ayrıca eklenecek)
    const netPrice = foreignNet * exchangeRate;
    const localPrice = netPrice * (1 + vatRate / 100);  // sadece görsel display için (KDV dahil)
    // Aynı stok aynı YEREL net fiyatla varsa birleştir; fiyat farklıysa ayrı satır aç
    const samePrice = (a, b) => Math.abs((parseFloat(a) || 0) - (parseFloat(b) || 0)) < 0.0001;
    const existing = lines.findIndex(l =>
      l.stock_id === stock.id && samePrice(l.unit_price, netPrice)
    );
    if (existing >= 0 && priceOverride == null) {
      const next = [...lines];
      next[existing] = { ...next[existing], quantity: (parseFloat(next[existing].quantity) || 0) + qty };
      setLines(next);
      setActiveLineIdx(existing);
    } else {
      const newLine = {
        id: Date.now() + Math.random(),
        stock_id: stock.id,
        stock_code: stock.code,
        stock_name: stock.name,
        image_url: stock.image_url || null,
        barcode: stock.barcode,
        unit_id: stock.unit_id,
        unit_code: stock.unit_code,
        color_id: stock.color_id || null,
        color_name: stock.color_name || null,
        size_id: stock.size_id || null,
        size_name: stock.size_name || null,
        personnel_id: activePersonnel?.id || null,
        personnel_name: activePersonnel?.full_name || null,
        quantity: qty,
        unit_price: netPrice,            // YEREL para birimi cinsinden net fiyat (backend için)
        display_price: localPrice,       // YEREL para birimi cinsinden KDV dahil fiyat
        // Yabancı döviz bilgisi (görsel için — net olarak sakla, görüntüde KDV eklenebilir)
        foreign_currency: isForeign ? stockCurrency : null,
        foreign_unit_price: isForeign ? foreignNet : null,    // ürünün kendi dövizinde NET fiyatı
        exchange_rate: exchangeRate,
        local_currency: localCurrency,
        vat_included: vatIncluded,
        vat_rate: vatRate,
        // Fiyat tier rehesabı için orijinal slotlar
        pd_price1: stock.pd_price1 != null ? parseFloat(stock.pd_price1) : null,
        pd_price2: stock.pd_price2 != null ? parseFloat(stock.pd_price2) : null,
        pd_price3: stock.pd_price3 != null ? parseFloat(stock.pd_price3) : null,
        pd_currency_code: stock.pd_currency_code || null,
        pd_vat_included: !!stock.pd_vat_included,
        price_tier: priceTier,
        discount_rate1: 0,
        discount_rate2: 0,
        discount_rate3: 0,
      };
      setLines(prev => [...prev, newLine]);
      setActiveLineIdx(lines.length);
    }
  };

  const updateLine = (idx, patch) => {
    const next = [...lines];
    next[idx] = { ...next[idx], ...patch };
    setLines(next);
  };

  const removeLine = (idx) => {
    setLines(prev => prev.filter((_, i) => i !== idx));
    setActiveLineIdx(null);
  };

  // ── Barkod scan / arama ─────────────────────────────────────────────
  // Desteklenen formatlar:
  //   • "8690..."            → 1 adet × barkod
  //   • "5*8690..."          → 5 adet × barkod (çoklu)
  //   • "27XXXXXQQQQQC"      → Tartı barkodu (settings.use_scale + weighted_product_start_code)
  //     PP(prefix) + BBBBB(stok kodu) + QQQQQ(miktar gram) + C(check) = 13 char
  const submitBarcode = async () => {
    if (!barcodeInput.trim()) return;
    const raw = barcodeInput.trim();
    let qty = 1;
    let barcode = raw;
    let forcedQty = null;  // tartı barkodundan gelen kilo

    // 1) Tartı barkodu kontrolü
    const useScale = String(appSettings?.use_scale || 'false').toLowerCase() === 'true';
    const startCode = (appSettings?.weighted_product_start_code || '').trim();
    if (useScale && startCode && raw.startsWith(startCode) && raw.length >= startCode.length + 10) {
      barcode = raw.substring(startCode.length, startCode.length + 5);
      const qtyRaw = parseInt(raw.substring(startCode.length + 5, startCode.length + 10)) || 0;
      forcedQty = qtyRaw / 1000;  // gram → kg
    } else {
      // 2) Çoklu miktar (X * BARKOD)
      const match = raw.match(/^(\d+(?:[.,]\d+)?)\s*[*xX×]\s*(.+)$/);
      if (match) {
        qty = parseFloat(match[1].replace(',', '.')) || 1;
        barcode = match[2].trim();
      }
    }

    try {
      const params = priceDef?.id ? '?retail_only=true&price_definition_id=' + priceDef.id : '?retail_only=true';
      const r = await api.get('/sales-invoices/stock-by-barcode/' + encodeURIComponent(barcode) + params);
      if (r.data.success) {
        const s = r.data.data;
        const unitMult = parseFloat(s.qty_multiplier) || 1;

        let totalQty;
        let msgInfo;
        if (forcedQty !== null && s.is_weighted) {
          // Tartı: kilo cinsinden gelir
          totalQty = forcedQty;
          msgInfo = `⚖ ${forcedQty} ${s.unit_code || 'kg'}`;
        } else if (unitMult > 1) {
          totalQty = qty * unitMult;
          msgInfo = qty > 1 ? `${qty} koli × ${unitMult} = ${totalQty}` : `${s.matched_unit_level}.birim ×${unitMult}`;
        } else {
          totalQty = qty;
          msgInfo = qty > 1 ? `${qty} adet` : '';
        }

        addLine(s, totalQty);
        setBarcodeInput('');
        setMsg({ type: 'success', text: `${s.code} — ${s.name}${msgInfo ? ' (' + msgInfo + ')' : ''}` });
        setTimeout(() => setMsg(null), 1500);
      }
    } catch (e) {
      setMsg({ type: 'error', text: 'Barkod bulunamadı: ' + barcode });
      setTimeout(() => setMsg(null), 2000);
    }
    setBarcodeInput('');
    barcodeRef.current?.focus();
  };

  // ── Sayısal tuş takımı ──────────────────────────────────────────────
  // Mod 'barcode': rakam → barkod input
  // Mod 'quantity'/'price'/'discount': rakam → buffer
  const numericPress = (key) => {
    const targetBarcode = editMode === 'barcode';
    if (key === 'C') {
      if (targetBarcode) setBarcodeInput(''); else setNumericBuffer('');
      return;
    }
    if (key === '←') {
      if (targetBarcode) setBarcodeInput(prev => prev.slice(0, -1));
      else setNumericBuffer(prev => prev.slice(0, -1));
      return;
    }
    if (key === '.') {
      if (targetBarcode) {
        if (!barcodeInput.includes('.')) setBarcodeInput(prev => prev + '.');
      } else {
        if (!numericBuffer.includes('.')) setNumericBuffer(prev => prev + '.');
      }
      return;
    }
    if (targetBarcode) setBarcodeInput(prev => prev + key);
    else setNumericBuffer(prev => prev + key);
  };

  const applyNumericToActive = () => {
    if (activeLineIdx == null || !numericBuffer) return;
    const val = parseFloat(numericBuffer) || 0;
    if (editMode === 'quantity') {
      updateLine(activeLineIdx, { quantity: val });
    } else if (editMode === 'price') {
      // Yetki kontrolü — kullanıcı kartında "Manuel Fiyat Değişikliği" kapalıysa bloke et
      if (user?.retailAllowPriceEdit === false) {
        setMsg({ type: 'error', text: 'Manuel fiyat değiştirme yetkiniz yok. Yöneticinize başvurun.' });
        setTimeout(() => setMsg(null), 3000);
        setEditMode('barcode');
        setNumericBuffer('');
        return;
      }
      updateLine(activeLineIdx, { unit_price: val });
    }
    setNumericBuffer('');
    setEditMode('barcode'); // uyguladıktan sonra barkod moduna geri dön
  };

  // Uygula butonu — barcode modundaysa barkodu ara, değilse buffer'ı satıra uygula
  const applyOrSearch = () => {
    if (editMode === 'barcode') {
      submitBarcode();
    } else {
      applyNumericToActive();
    }
  };

  // ── Satışı Beklet / Bekleyenden geri yükle ────────────────────────
  const parkCurrent = () => {
    if (lines.length === 0) { setMsg({ type: 'error', text: 'Sepet boş — bekletilecek satış yok' }); setTimeout(() => setMsg(null), 2000); return; }
    const label = window.prompt('Bekletilen satışa etiket ver (boş bırakılabilir):',
      (customer?.title || customer?.name || activePersonnel?.full_name || 'Müşteri')
    );
    if (label === null) return;  // iptal
    const entry = {
      id: 'park_' + Date.now(),
      label: label?.trim() || 'Beklemede',
      savedAt: new Date().toISOString(),
      lines, customer, activePersonnel, headerDisc,
      grandTotal: totals.grandTotal,
    };
    const next = [entry, ...parkedSales].slice(0, 50);  // max 50 kayıt
    setParkedSales(next);
    try { localStorage.setItem('pos_parked_sales', JSON.stringify(next)); } catch {}
    // Sepeti temizle
    setLines([]); setActiveLineIdx(null); setNumericBuffer(''); setCustomer(null);
    setHeaderDisc({ d1: { type: 'percent', value: 0 }, d2: { type: 'percent', value: 0 }, d3: { type: 'percent', value: 0 }, rounding: { type: 'amount', value: 0 } });
    setMsg({ type: 'success', text: 'Satış beklemeye alındı: ' + entry.label });
    setTimeout(() => setMsg(null), 2000);
  };

  const restoreParked = (entry) => {
    if (lines.length > 0 && !window.confirm('Mevcut sepet temizlenip bekletilen satış yüklenecek. Devam?')) return;
    setLines(entry.lines || []);
    setCustomer(entry.customer || null);
    setActivePersonnel(entry.activePersonnel || null);
    setHeaderDisc(entry.headerDisc || { d1: { type: 'percent', value: 0 }, d2: { type: 'percent', value: 0 }, d3: { type: 'percent', value: 0 }, rounding: { type: 'amount', value: 0 } });
    // Park listesinden kaldır
    const next = parkedSales.filter(p => p.id !== entry.id);
    setParkedSales(next);
    try { localStorage.setItem('pos_parked_sales', JSON.stringify(next)); } catch {}
    setShowParked(false);
    setMsg({ type: 'success', text: 'Satış yüklendi: ' + entry.label });
    setTimeout(() => setMsg(null), 2000);
  };

  const deleteParked = (id) => {
    if (!window.confirm('Bekletilen satış silinsin mi?')) return;
    const next = parkedSales.filter(p => p.id !== id);
    setParkedSales(next);
    try { localStorage.setItem('pos_parked_sales', JSON.stringify(next)); } catch {}
  };

  // ── Aksiyon: işlemi iptal et / satırı sil ──────────────────────────
  const voidLine = () => {
    if (activeLineIdx == null) return;
    if (!window.confirm('Seçili satır silinsin mi?')) return;
    removeLine(activeLineIdx);
  };

  const cancelTransaction = () => {
    if (lines.length === 0) return;
    if (!window.confirm('Tüm sepet temizlensin mi? Bu işlem geri alınamaz.')) return;
    setLines([]); setActiveLineIdx(null); setNumericBuffer(''); setCustomer(null);
    setPriceTier(1);  // iptalden sonra default peşin
    setHeaderDisc({
      d1: { type: 'percent', value: 0 },
      d2: { type: 'percent', value: 0 },
      d3: { type: 'percent', value: 0 },
      rounding: { type: 'amount', value: 0 },
    });
  };

  // ── Fiyat tipi değiştirme (peşin ↔ vadeli) ───────────────────────────
  const recomputeLineForTier = (l, newTier) => {
    const localCurrency = (l.local_currency || appSettings?.local_currency || priceDef?.currency_code || 'TRY').toUpperCase();
    const stockCurrency = (l.pd_currency_code || priceDef?.currency_code || localCurrency).toUpperCase();
    const isForeign = stockCurrency !== localCurrency;
    const exchangeRate = isForeign
      ? parseFloat(exchangeRates?.[stockCurrency]?.selling_rate || exchangeRates?.[stockCurrency]?.mid_rate) || 1
      : 1;
    const slot = 'pd_price' + newTier;
    let unitPrice = l[slot];
    if (unitPrice == null || parseFloat(unitPrice) <= 0) unitPrice = l.pd_price1;
    unitPrice = parseFloat(unitPrice) || 0;
    const vatRate = parseFloat(l.vat_rate) || 0;
    const vatIncluded = !!l.pd_vat_included;
    const foreignGross = unitPrice;
    const foreignNet = vatIncluded && foreignGross > 0
      ? +(foreignGross / (1 + vatRate / 100)).toFixed(4)
      : foreignGross;
    const netPrice = foreignNet * exchangeRate;
    const localPrice = netPrice * (1 + vatRate / 100);
    return {
      ...l,
      unit_price: netPrice,
      display_price: localPrice,
      foreign_unit_price: isForeign ? foreignNet : null,
      exchange_rate: exchangeRate,
      price_tier: newTier,
    };
  };

  const requestTierChange = (newTier) => {
    if (newTier === priceTier) return;
    if (lines.length > 0) {
      setShowTierChangeConfirm({ from: priceTier, to: newTier });
    } else {
      setPriceTier(newTier);
    }
  };

  const applyTierChange = (newTier, recomputeExisting) => {
    setPriceTier(newTier);
    if (recomputeExisting && lines.length > 0) {
      setLines(prev => prev.map(l => recomputeLineForTier(l, newTier)));
    }
    setShowTierChangeConfirm(null);
  };

  // ── Ödeme ────────────────────────────────────────────────────────────
  const openPayment = async () => {
    if (lines.length === 0) { setMsg({ type: 'error', text: 'Sepet boş' }); return; }
    // Zorunlu ayarlar
    if (!customer && !defaultCustomer) {
      setMsg({ type: 'error', text: '⚠ Ayarlar → Perakende Satış → Varsayılan Cari tanımlayın (veya bu satışa müşteri seçin)' });
      setTimeout(() => setMsg(null), 5000);
      return;
    }
    if (!warehouseId) {
      setMsg({ type: 'error', text: '⚠ Depo tanımlı değil. Kullanıcı kartınızda veya Ayarlar → Perakende Satış varsayılan depo seçin' });
      setTimeout(() => setMsg(null), 5000);
      return;
    }
    if (!cashRegisterId) {
      setMsg({ type: 'error', text: '⚠ Kasa tanımlı değil. Kullanıcı kartınızda veya Ayarlar → Perakende Satış varsayılan kasa seçin' });
      setTimeout(() => setMsg(null), 5000);
      return;
    }

    // PRE-FLIGHT stok yeterlilik kontrolü — ödeme dialog'una geçmeden önce.
    // OKC / e-fatura / bilgi fişi işlemlerinden ÖNCE buradan bloklanır.
    try {
      const stockCheck = await api.post('/stocks/check-availability', {
        warehouse_id: warehouseId,
        lines: lines
          .filter(l => l.stock_id && parseFloat(l.quantity) > 0)
          .map(l => ({
            stock_id: l.stock_id,
            stock_name: l.stock_name || l.name,
            quantity: parseFloat(l.quantity) || 0,
          })),
      });
      if (!stockCheck.data?.ok) {
        const issues = stockCheck.data?.issues || [];
        const issueLines = issues.map(it => {
          const code = it.stock_code ? `[${it.stock_code}]` : '';
          const name = it.stock_name || '';
          const label = [code, name].filter(Boolean).join(' ').trim() || '(stok)';
          return `• ${label} — Mevcut: ${it.available}, Talep: ${it.required}, Eksik: ${it.shortage}`;
        }).join('\n');
        setMsg({
          type: 'error',
          text: `⚠ Stok yetersiz — ödeme alınamaz:\n${issueLines}`,
        });
        setTimeout(() => setMsg(null), 10000);
        return;
      }
    } catch (e) {
      // Endpoint hatası → engellemeyelim, ileride backend yine yakalar
      console.warn('preflight stock check error:', e.message);
    }

    setShowPayment(true);
  };

  // splits: { cash, on_account, cards:[{amount,pos_definition_id,installment_count,card_type}],
  //           transfers:[{amount,bank_account_id}], vouchers:[{amount,shopping_voucher_id}] }
  // dispatch-mode kararı: kasa varsa OKC, yoksa HB ayarı kontrolü → efatura/earsiv ya da bilgi_fisi
  const resolveDispatchMode = async () => {
    try {
      const branchId = appSettings?.retail_default_branch_id || user?.defaultBranchId || '';
      const customerId = customer?.id || '';
      const hasTerm = activeTerminalId ? 'yes' : 'no';
      const r = await api.get('/retail-sale/dispatch-mode', {
        params: { branch_id: branchId, customer_id: customerId, has_terminal: hasTerm },
      });
      return r.data?.data;
    } catch (e) {
      console.error('dispatch-mode:', e);
      return { mode: 'bilgi_fisi', reason: 'API hatası — bilgi fişi default', missing: ['dispatch-mode endpoint'] };
    }
  };

  const completePayment = async (splits, opts = {}) => {
    if (!warehouseId) { setMsg({ type: 'error', text: 'Önce depo seçin' }); return; }
    setSaving(true);

    // ─── İSKONTO + YUVARLAMA HESABI — header iskontolarını her satıra dağıt ────
    // Bu computedLines hem OKC payload'ı hem sales_invoice POST'u için kullanılır.
    // Yuvarlama tabanı: satır iskontoları + header iskontoları sonrası.
    const _baseForRnd  = (totals.grandTotal + (totals.rnd || 0));
    const _rndAmt      = totals.rnd || 0;
    const _rndPercent  = _baseForRnd > 0 ? (_rndAmt / _baseForRnd) * 100 : 0;
    const _headerSubtotal = (totals.total - totals.lineDiscTotal);
    const _hCalc = (d, base) => {
      if (!d || !d.value) return 0;
      if (d.type === 'percent') return parseFloat(d.value) || 0;
      return base > 0 ? ((parseFloat(d.value) || 0) * 100 / base) : 0;
    };
    const _hr1 = discountLevels >= 1 ? _hCalc(headerDisc.d1, _headerSubtotal) : 0;
    const _after1 = _headerSubtotal * (1 - _hr1 / 100);
    const _hr2 = discountLevels >= 2 ? _hCalc(headerDisc.d2, _after1) : 0;
    const _after2 = _after1 * (1 - _hr2 / 100);
    const _hr3 = discountLevels >= 3 ? _hCalc(headerDisc.d3, _after2) : 0;
    const _r5  = roundingEnabled ? (headerDisc.rounding?.type === 'percent'
      ? (parseFloat(headerDisc.rounding?.value) || 0)
      : _rndPercent) : 0;
    const _combine = (lineRate, headerRate) => {
      const lr = parseFloat(lineRate) || 0;
      const hr = parseFloat(headerRate) || 0;
      if (lr === 0 && hr === 0) return 0;
      return (1 - (1 - lr / 100) * (1 - hr / 100)) * 100;
    };
    const computedLines = lines.map((l, i) => {
      const qty = parseFloat(l.quantity) || 0;
      const unitPriceNet = parseFloat(l.unit_price) || 0;
      const vatRate = parseFloat(l.vat_rate) || 0;
      const unitPriceGross = unitPriceNet * (1 + vatRate / 100);
      const d1 = _combine(l.discount_rate1, _hr1);
      const d2 = _combine(l.discount_rate2, _hr2);
      const d3 = _combine(l.discount_rate3, _hr3);
      const d5 = _r5;
      const lineSubtotal = qty * unitPriceNet;
      const afterDisc = lineSubtotal * (1 - d1 / 100) * (1 - d2 / 100) * (1 - d3 / 100) * (1 - d5 / 100);
      const lineDiscount = lineSubtotal - afterDisc;
      const lineNet = afterDisc;
      const lineVatBase = lineNet;
      const lineVat = lineVatBase * vatRate / 100;
      const lineTotal = lineNet + lineVat;
      return {
        line_no: i + 1,
        stock_id: l.stock_id, unit_id: l.unit_id, unit_type: 'unit1', unit_ratio: 1,
        color_id: l.color_id || null, size_id: l.size_id || null,
        personnel_id: l.personnel_id || activePersonnel?.id || null,
        barcode: l.barcode || null,
        name: l.name || l.stock_name || '',
        quantity: qty, quantity_base: qty,
        unit_price: unitPriceGross, unit_price_net: unitPriceNet,
        is_vat_included: 1,
        vat_rate: vatRate, vat_amount: lineVat,
        discount_rate1: d1, discount_rate2: d2, discount_rate3: d3,
        discount_rate4: 0, discount_rate5: d5,
        discount_amount: lineDiscount,
        otv_rate: 0, otv_amount: 0,
        withholding_rate: 0, withholding_amount: 0,
        line_subtotal: lineSubtotal,
        line_discount: lineDiscount,
        line_net: lineNet,
        line_otv: 0,
        line_vat_base: lineVatBase, line_vat: lineVat,
        line_withholding: 0,
        line_total: lineTotal,
        warehouse_id: warehouseId,
      };
    });

    // YN ÖKC akışı: aktif kasa seçili ise ÖNCE Protel'e gönder; başarılıysa sonra retail_sale yaz.
    let okcResult = null;
    const activeTerminal = posTerminals.find(t => t.id === activeTerminalId);

    // KASA YOK senaryosu — dispatch-mode'a göre dallan
    if (!activeTerminal && !opts.skipDispatchMode) {
      const dispatch = await resolveDispatchMode();
      if (dispatch?.mode === 'bilgi_fisi') {
        // Modal'ı aç, kullanıcı karar versin (yazdır / sadece kaydet / iptal)
        setSaving(false);
        setInfoSlipPrompt({
          splits,
          missing: dispatch.missing || [],
          taxpayer: dispatch.taxpayer || {},
          reason: dispatch.reason,
        });
        return;
      }
      // efatura veya earsiv → invoice POST'unda flag set edilecek (body.auto_einvoice_queue + e_doc_type)
      opts.autoEInvoice = {
        doc_type: dispatch?.doc_type || (dispatch?.mode === 'efatura' ? 'eFatura' : 'eArsiv'),
        mode: dispatch?.mode,
      };
    }

    if (activeTerminal) {
      try {
        // CheckSeq frontend'te üret — overlay üzerinde manuel iptal için
        const d = new Date();
        const checkSeq = parseInt(
          String(d.getHours()).padStart(2, '0') +
          String(d.getMinutes()).padStart(2, '0') +
          String(d.getSeconds()).padStart(2, '0') +
          String(d.getMilliseconds()).padStart(3, '0')
        ) || Date.now() % 1000000000;
        setPosOverlay({ stage: 'starting', text: 'Kasaya gönderiliyor...', check_seq: checkSeq });
        // Protel payload — kasa fişinde iskonto/yuvarlama görünsün diye BRÜT total + ayrı discount.
        //   total    = KDV dahil iskontosuz (brüt) — fişte "Birim x Adet" satırı
        //   discount = KDV dahil iskonto + yuvarlama toplamı — fişte "İndirim" satırı
        //   tax      = iskonto sonrası net üzerindeki KDV
        // Protel: net_to_pay = total - discount = bizim grandTotal'ımız.
        const okcPayload = {
          check_seq: checkSeq,
          total: totals.grandTotal,
          rvc_id: parseInt(activeTerminal.rvc_id) || 1,
          sale_items: computedLines.map((c) => {
            const vatFactor = 1 + (parseFloat(c.vat_rate) || 0) / 100;
            const grossPreDisc = c.line_subtotal * vatFactor;     // KDV dahil iskontosuz
            const grossDisc    = grossPreDisc - c.line_total;     // KDV dahil iskonto+yuvarlama toplamı
            return {
              plu: c.line_no,
              name: (c.name || '').substring(0, 30),
              quantity: c.quantity,
              total: grossPreDisc,
              tax:   c.line_vat,
              vat_rate: c.vat_rate,
              ...(grossDisc > 0.01 ? { discount: grossDisc } : {}),
            };
          }),
          payments: [
            ...((splits.cash || 0) > 0.01 ? [{ tip: 'NAKIT', amount: splits.cash }] : []),
            ...(splits.cards || []).filter(c => (c.amount || 0) > 0.01).map(c => ({
              tip: 'KART', amount: c.amount, installment_count: parseInt(c.installment_count) || 1,
            })),
            ...(splits.transfers || []).filter(c => (c.amount || 0) > 0.01).map(c => ({
              tip: 'HAVALE', amount: c.amount,
            })),
            ...(splits.vouchers || []).filter(c => (c.amount || 0) > 0.01).map(c => ({
              tip: 'HEDIYE_CEKI', amount: c.amount,
            })),
          ],
          invoice: {
            active: true,
            invoice_type: '2', // 2 = e-Arşiv default — perakende için tipik
            customer_name: (customer?.title || defaultCustomer?.title || 'PERAKENDE SATIS').substring(0, 200),
            tax_number: customer?.tax_number || '11111111111',
            tax_office: customer?.tax_office || 'MERKEZ',
            address1: (customer?.address || '').substring(0, 200),
            city: customer?.city || 'İstanbul',
            county: customer?.district || '',
            country: 'Türkiye',
            email: customer?.email || '',
          },
        };
        // Ödeme yöntemine göre dinamik mesaj
        const hasCard     = (splits.cards     || []).some(c => (c.amount || 0) > 0.01);
        const hasCash     = (splits.cash      || 0) > 0.01;
        const hasTransfer = (splits.transfers || []).some(c => (c.amount || 0) > 0.01);
        const hasVoucher  = (splits.vouchers  || []).some(c => (c.amount || 0) > 0.01);
        let overlayText = 'Kasaya gönderildi...';
        if (hasCard) {
          overlayText = 'Kasaya gönderildi\n💳 Lütfen Kredi Kartını Okutun';
        } else if (hasCash && !hasTransfer && !hasVoucher) {
          overlayText = 'Kasaya gönderildi\n💵 Nakit ödeme bekleniyor';
        } else if (hasTransfer && !hasCash && !hasVoucher) {
          overlayText = 'Kasaya gönderildi\n🏦 Havale işleniyor';
        } else if (hasVoucher && !hasCash && !hasTransfer) {
          overlayText = 'Kasaya gönderildi\n🎁 Hediye çeki işleniyor';
        }
        setPosOverlay({ stage: 'processing', text: overlayText, check_seq: checkSeq, hasCard });
        const r = await api.post('/pos-sale', { terminal_id: activeTerminal.id, payload: okcPayload }, {
          // Kasa işlemi 30-300 sn sürebilir, axios default 15sn'yi geçer
          timeout: ((parseInt(activeTerminal.polling_max_sec) || 300) + 30) * 1000,
        });
        if (!r.data?.success) {
          const errMsg = r.data?.data?.error || r.data?.message || 'bilinmeyen hata';
          setPosOverlay({ stage: 'error', text: '❌ ' + errMsg, autoClose: true });
          setTimeout(() => { setPosOverlay(null); }, 5000);
          setSaving(false);
          return;
        }
        okcResult = r.data.data;
        setPosOverlay({ stage: 'done', text: '✓ Kasa onaylandı — kayıt yapılıyor...' });
      } catch (e) {
        const errMsg = e.response?.data?.message || e.message;
        setPosOverlay({ stage: 'error', text: '❌ ' + errMsg, autoClose: true });
        setTimeout(() => { setPosOverlay(null); }, 5000);
        setSaving(false);
        return;
      }
    }

    try {
      // computedLines yukarıda zaten hesaplandı (OKC + POST aynı veriyi kullanır)
      // Header toplamları
      const hdr = computedLines.reduce((a, c) => {
        a.subtotal      += c.line_subtotal;
        a.discount_total+= c.line_discount;
        a.net_total     += c.line_net;
        a.vat_total     += c.line_vat;
        a.grand_total   += c.line_total;
        return a;
      }, { subtotal: 0, discount_total: 0, net_total: 0, vat_total: 0, grand_total: 0 });

      // KDV oranı bazında özet (vat_breakdown)
      const vatMap = {};
      computedLines.forEach((c) => {
        const k = String(c.vat_rate || 0);
        if (!vatMap[k]) vatMap[k] = { rate: c.vat_rate, base: 0, amount: 0 };
        vatMap[k].base   += c.line_vat_base;
        vatMap[k].amount += c.line_vat;
      });
      const vatBreakdown = Object.values(vatMap);

      const body = {
        invoice_type: 'retail_sale',
        invoice_date: new Date().toISOString().slice(0, 10),
        warehouse_id: warehouseId,
        account_id: customer?.id || defaultCustomer?.id || null,
        personnel_id: activePersonnel?.id || null,
        currency_code: priceDef?.currency_code || 'TRY',
        exchange_rate: 1,
        local_currency: priceDef?.currency_code || 'TRY',
        is_vat_included: 0,
        status: 'approved',
        bypass_approval: true,
        skip_hierarchy: true,
        record_to_stock: true,
        record_to_account: true,
        // Header toplamları
        subtotal:       hdr.subtotal,
        discount_total: hdr.discount_total,
        net_total:      hdr.net_total,
        otv_total:      0,
        vat_total:      hdr.vat_total,
        withholding_total: 0,
        grand_total:    hdr.grand_total,
        vat_breakdown:  vatBreakdown,
        // Raporlama
        retail_disc1: headerDisc.d1, retail_disc2: headerDisc.d2, retail_disc3: headerDisc.d3,
        retail_rounding: headerDisc.rounding,
        lines: computedLines,
        payment_method: 'split',
        received_amount: (splits.cash || 0)
          + (splits.cards || []).reduce((s, c) => s + (c.amount || 0), 0)
          + (splits.transfers || []).reduce((s, c) => s + (c.amount || 0), 0)
          + (splits.vouchers || []).reduce((s, c) => s + (c.amount || 0), 0),
        // YN ÖKC kasası kullanıldıysa metadata
        ...(okcResult ? {
          pos_terminal_id: activeTerminal.id,
          okc_check_seq:   okcResult.check_seq,
          okc_invoice_no:  okcResult.invoice_no,
          okc_ett_no:      okcResult.ett_no,
          okc_z_no:        okcResult.z_no,
          okc_eku_no:      okcResult.eku_no,
          okc_invoice_url: okcResult.invoice_url,
          okc_receipt_no:  okcResult.receipt_no,
        } : {}),
        // Kasa yok + e-fatura mümkün → otomatik kuyruğa al
        ...(opts.autoEInvoice ? {
          auto_einvoice_queue: true,
          e_doc_type: opts.autoEInvoice.doc_type,
        } : {}),
      };
      const r = await api.post('/sales-invoices', body);
      if (r.data.success) {
        const invoiceId = r.data.data?.id;
        const invoiceNo = r.data.data?.document_number || invoiceId;

        // Bilgi fişi yazdırma talebi varsa 80mm pencere aç
        if (opts.infoSlipPrint) {
          printInfoSlip({
            invoiceNo,
            invoiceDate: new Date(),
            customer: customer?.title || defaultCustomer?.title || 'PERAKENDE SATIS',
            customerVkn: customer?.tax_number || customer?.id_number || '',
            taxpayer: opts.infoSlipTaxpayer || {},
            lines,
            totals,
            splits,
          });
        }

        // Otomatik Cari Tahsilat Makbuzu oluştur (cariye yazılan kısım hariç ödenen tutarlar için)
        const receiptLines = [];
        if ((splits.cash || 0) > 0.01 && cashRegisterId) {
          receiptLines.push({ payment_method: 'cash', amount: splits.cash, cash_register_id: cashRegisterId });
        }
        // Çoklu POS — her kredi kartı satırı kendi pos_definition_id'si ve taksitiyle ayrı receipt line
        // Backend otomatik pos_slips kaydı, komisyon hesaplaması (rate + BSMV), expected_collection_date yapar
        (splits.cards || []).forEach((c) => {
          if (c.amount > 0.01 && c.pos_definition_id) {
            receiptLines.push({
              payment_method: 'credit_card',
              amount: c.amount,
              pos_definition_id: c.pos_definition_id,
              installment_count: c.installment_count || 1,
              card_type: c.card_type || 'credit',
            });
          }
        });
        // Çoklu Banka Havale — her satır kendi banka_account_id'si ile
        (splits.transfers || []).forEach((c) => {
          if (c.amount > 0.01 && c.bank_account_id) {
            receiptLines.push({ payment_method: 'bank_transfer', amount: c.amount, bank_account_id: c.bank_account_id });
          }
        });
        // Alış-Veriş Çekleri — her satır müşterinin issued voucher'ından
        (splits.vouchers || []).forEach((c) => {
          if (c.amount > 0.01 && c.shopping_voucher_id) {
            receiptLines.push({ payment_method: 'shopping_voucher', amount: c.amount, shopping_voucher_id: c.shopping_voucher_id });
          }
        });

        const receiptAccountId = customer?.id || defaultCustomer?.id;
        if (receiptLines.length > 0 && receiptAccountId) {
          try {
            await api.post('/cari-receipts', {
              account_id: receiptAccountId,
              date: new Date().toISOString().slice(0, 10),
              description: `PoS Satış ${invoiceNo}`,
              notes: 'Otomatik oluşturuldu — perakende satıştan',
              lines: receiptLines,
              source_invoice_id: invoiceId,
              source_invoice_type: 'retail_sale',
            });
          } catch (recErr) {
            setMsg({ type: 'warning', text: `Satış kaydedildi (${invoiceNo}) ama tahsilat makbuzu oluşmadı: ${recErr.response?.data?.message || recErr.message}` });
            setTimeout(() => setMsg(null), 6000);
          }
        } else if (receiptLines.length > 0 && !receiptAccountId) {
          setMsg({ type: 'warning', text: `Satış kaydedildi (${invoiceNo}) — Tahsilat makbuzu için cari hesap atanmamış (ayarlardan perakende default cari seçin).` });
          setTimeout(() => setMsg(null), 6000);
        }

        setMsg({ type: 'success', text: `Satış tamamlandı: ${invoiceNo}${splits.on_account > 0.01 ? ` (₺${splits.on_account.toFixed(2)} cariye)` : ''}` });
        setLines([]); setActiveLineIdx(null); setNumericBuffer(''); setCustomer(null);
        setPriceTier(1);  // satıştan sonra default peşin
        setHeaderDisc({
          d1: { type: 'percent', value: 0 },
          d2: { type: 'percent', value: 0 },
          d3: { type: 'percent', value: 0 },
          rounding: { type: 'amount', value: 0 },
        });
        setShowPayment(false);
        setTimeout(() => setMsg(null), 2500);
        barcodeRef.current?.focus();
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Satış kaydedilemedi' });
    } finally { setSaving(false); setPosOverlay(null); }
  };

  // ── Ön-kontrol: Açılış için gerekli tanımlar ─────────────────────
  // Kullanıcı kartı / Ayarlar / Şubeler / Fiyat Tanımları'nda gerekli kayıtlar yoksa
  // PoS ekranı açılmasın, kullanıcıya hangisinin eksik olduğunu göster.
  const missing = [];
  if (!defaultCustomer?.id && !customer?.id)         missing.push({ key: 'cari',  label: 'Perakende Varsayılan Cari',      fix: 'Ayarlar → 🛒 Perakende Satış Ayarları → Varsayılan Cari (Perakende Müşteri)' });
  if (!warehouseId)                                  missing.push({ key: 'depo',  label: 'Perakende Varsayılan Depo',       fix: 'Kullanıcı kartı veya Ayarlar → Perakende Satış Ayarları → Varsayılan Depo' });
  if (!cashRegisterId)                               missing.push({ key: 'kasa',  label: 'Perakende Varsayılan Kasa',       fix: 'Kullanıcı kartı veya Ayarlar → Perakende Satış Ayarları → Varsayılan Kasa' });
  const _branchId = appSettings?.retail_default_branch_id || user?.defaultBranchId;
  if (!_branchId)                                    missing.push({ key: 'sube',  label: 'Perakende Varsayılan Şube',       fix: 'Ayarlar → Perakende Satış Ayarları → Varsayılan Şube (Perakende), ya da Kullanıcı kartı → Varsayılan Şube' });
  if (!priceDef?.id)                                 missing.push({ key: 'fiyat', label: 'Perakende Fiyat Tanımı (price_group=perakende)', fix: 'Program Tanımları → Fiyat Tanımları → "perakende" fiyat grubunda en az bir aktif tanım' });

  if (missing.length > 0) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100%', padding: 20, background: 'var(--bg-secondary)',
      }}>
        <div style={{
          background: 'var(--bg-card)', borderRadius: 14, padding: 28,
          maxWidth: 720, width: '100%',
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          border: '1px solid var(--border-secondary)',
        }}>
          <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 8 }}>⚠️</div>
          <h2 style={{ textAlign: 'center', margin: '0 0 6px 0', fontSize: '1.4rem', color: 'var(--text-primary)' }}>
            Perakende Satış Açılamadı
          </h2>
          <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: 18, fontSize: 14 }}>
            Aşağıdaki <strong>{missing.length} eksik tanım</strong> giderilmeden PoS ekranı çalışamaz.
            Önce ilgili kayıtları oluşturup yine deneyin.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
            {missing.map(m => (
              <div key={m.key} style={{
                background: 'var(--warning-bg, rgba(245, 158, 11, 0.10))',
                border: '1px solid var(--warning, #f59e0b)',
                borderRadius: 10, padding: '12px 14px',
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#b45309', marginBottom: 4 }}>
                  ❌ {m.label}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  <strong>Nasıl:</strong> {m.fix}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.assign('/settings')}
              style={{
                padding: '10px 18px', background: 'var(--accent-primary)', color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}>⚙ Ayarlara Git</button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 18px', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                border: '1px solid var(--border-secondary)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}>↻ Tanımları Tekrar Kontrol Et</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="pos-root" style={{
      display: 'grid',
      gridTemplateRows: 'auto 1fr',
      height: '100%',
      minHeight: 0,
      background: 'var(--bg-secondary)',
      fontSize: '14px',
      overflow: 'hidden',
    }}>
      {/* Mobil için CSS — yan yana kalır, dar ekranda tüm sayfa yatay scroll açılır */}
      <style>{`
        @media (max-width: 900px) {
          .pos-root {
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            height: 100% !important;
            min-height: 0;
          }
          .pos-root .pos-topbar {
            font-size: 11px !important;
            padding: 6px 8px !important;
            min-width: 880px;
            flex-wrap: nowrap;
          }
          .pos-root .pos-topbar > div {
            font-size: 11px !important;
            flex-wrap: nowrap;
            min-width: max-content;
          }
          .pos-root .pos-main {
            grid-template-columns: 380px 480px !important;
            min-width: 880px;
            height: auto;
            min-height: 0;
          }
        }
      `}</style>
      {/* ─── ÜST BAR ─── */}
      <div className="pos-topbar" style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'center',
        padding: '6px 14px', background: 'var(--bg-card)',
        borderBottom: '1px solid var(--border-secondary)',
        fontSize: '12px',
      }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
          {(() => {
            const c = customer || defaultCustomer;
            const isDefault = !customer && !!defaultCustomer;
            return c ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                👤 <strong>{c.title || c.name}</strong>
                {isDefault && <span style={{ fontSize: 10, color: 'var(--text-tertiary)', fontWeight: 500 }}>(varsayılan)</span>}
              </span>
            ) : (
              <span style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--danger)', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }} title="Ayarlar → Perakende Satış Ayarları → Varsayılan Cari'yi tanımlayın">
                ⚠ Varsayılan cari yok
              </span>
            );
          })()}
          <span>🏬 {warehouses.find(w => w.id === warehouseId)?.name || <span style={{ color: 'var(--danger)' }}>⚠ Depo yok</span>}</span>
          {(() => {
            const branchId = appSettings?.retail_default_branch_id || user?.defaultBranchId;
            const branch = branches.find(b => b.id === branchId);
            if (!branchId) return (
              <span style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--danger)', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }} title="Ayarlar → Perakende Satış Ayarları → Varsayılan Şube'yi tanımlayın">
                ⚠ Şube yok
              </span>
            );
            return (
              <span title="Bu PoS cihazının atandığı şube">
                🏢 {branch?.name || branchId.slice(0, 6)}
                {branch?.is_dealer && <span style={{ marginLeft: 4, fontSize: 10, padding: '1px 6px', background: 'rgba(99,102,241,0.15)', color: '#6366f1', borderRadius: 4, fontWeight: 700 }}>BAYİ</span>}
              </span>
            );
          })()}
          {cashRegisterId ? (
            <span>💵 {cashRegisters.find(c => c.id === cashRegisterId)?.register_name || '—'}</span>
          ) : (
            <span style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--danger)', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>⚠ Kasa yok</span>
          )}
          {posTerminals.length > 0 && (
            <span title="Yazar Kasa (YN ÖKC) — satışlar bu kasadan e-belge keser" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', padding: '2px 6px', borderRadius: 4, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              🏪
              <select value={activeTerminalId} onChange={e => setActiveTerminalId(e.target.value)}
                style={{ background: 'transparent', border: 'none', color: 'inherit', fontWeight: 700, fontSize: 12, outline: 'none', cursor: 'pointer' }}>
                <option value="">— Kasa yok (ERP keser) —</option>
                {posTerminals.map(t => (
                  <option key={t.id} value={t.id}>{t.code} · {t.name}</option>
                ))}
              </select>
            </span>
          )}
          {priceDef ? (
            <span title={`Fiyat tanımı: ${priceDef.name}`} style={{ background: 'rgba(99,102,241,0.12)', color: '#6366f1', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
              💰 {priceDef.name} ({priceDef.currency_code || 'TRY'})
            </span>
          ) : (
            <span style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }} title="Perakende fiyat tanımı bulunamadı — fiyatlar 0 olarak başlar, manuel girin">
              ⚠ Fiyat tanımı yok
            </span>
          )}
          <button onClick={() => { setPersonnelPickTarget(null); setShowPersonnelPick(true); }} style={{
            background: activePersonnel ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.12)',
            color: activePersonnel ? 'var(--success)' : 'var(--text-secondary)',
            padding: '2px 10px', borderRadius: 4, fontWeight: 600,
            border: '1px solid ' + (activePersonnel ? 'rgba(16,185,129,0.35)' : 'var(--border-secondary)'),
            cursor: 'pointer', fontSize: '12px',
          }} title="Satıra atanacak satış personeli">
            👨‍💼 {activePersonnel ? activePersonnel.full_name : 'Personel Seç'}
          </button>
          <span>📅 {dateStr} · {timeStr}</span>
        </div>
        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 1 }}>
          {activeLineIdx != null ? '✎ DÜZENLEME MODU' : '⌗ HIZLI PERAKENDE SATIŞ'}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '11px' }}>
          <span style={{ color: 'var(--text-tertiary)' }}>Kasiyer:</span>
          <strong>{user?.full_name || user?.username || '—'}</strong>
        </div>
      </div>

      {/* ─── ANA İÇERİK ─── */}
      <div className="pos-main" style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(360px, 1fr) minmax(440px, 1fr)',
        gap: 8, padding: 8, overflow: 'hidden',
      }}>
        {/* SOL: SEPET */}
        <div className="pos-cart" style={{
          background: 'var(--bg-card)', borderRadius: 8,
          border: '1px solid var(--border-secondary)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          minHeight: 0,
        }}>
          {/* TOPLAM — sepetin üstünde */}
          {(() => {
            const cur = curSym(priceDef?.currency_code);
            return (
              <div style={{
                padding: '10px 18px',
                background: 'var(--accent-primary)', color: '#fff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div style={{ fontSize: '11px', opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Net: {fmt(totals.subtotal)} {cur} · KDV: {fmt(totals.vat)} {cur}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '10px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Genel Toplam (KDV Dahil)
                    </div>
                    <div style={{ fontSize: '2.2rem', fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, lineHeight: 1 }}>
                      {fmt(totals.grandTotal)} {cur}
                    </div>
                  </div>
                </div>
                {(totals.lineDiscTotal > 0 || totals.disc1 > 0 || totals.disc2 > 0 || totals.disc3 > 0 || totals.rnd !== 0) && (
                  <div style={{ fontSize: '11px', marginTop: 6, display: 'flex', gap: 10, opacity: 0.9, flexWrap: 'wrap' }}>
                    <span>Brüt: {fmt(totals.total)} {cur}</span>
                    {totals.lineDiscTotal > 0 && <span>Satır İsk.: -{fmt(totals.lineDiscTotal)} {cur}</span>}
                    {totals.disc1 > 0 && <span>İsk1: -{fmt(totals.disc1)} {cur}</span>}
                    {totals.disc2 > 0 && <span>İsk2: -{fmt(totals.disc2)} {cur}</span>}
                    {totals.disc3 > 0 && <span>İsk3: -{fmt(totals.disc3)} {cur}</span>}
                    {totals.rnd !== 0 && <span>Yuvarlama: {totals.rnd > 0 ? '-' : '+'}{fmt(Math.abs(totals.rnd))} {cur}</span>}
                  </div>
                )}
              </div>
            );
          })()}

          <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ minWidth: 'max-content', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '32px 44px minmax(180px,1fr) 80px 80px 90px 44px',
                padding: '8px 10px', background: 'var(--bg-secondary)',
                borderBottom: '1px solid var(--border-secondary)',
                fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
              }}>
                <div>#</div>
                <div></div>
                <div>ÜRÜN</div>
                <div style={{ textAlign: 'right' }}>MIKTAR</div>
                <div style={{ textAlign: 'right' }}>FİYAT</div>
                <div style={{ textAlign: 'right' }}>TOPLAM</div>
                <div></div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {lines.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🛒</div>
                <div>Sepet boş</div>
                <div style={{ fontSize: '11px', marginTop: 6 }}>Barkod okutun veya favorilerden seçin</div>
              </div>
            ) : (
              lines.map((l, i) => {
                // FİYAT ve TOPLAM her zaman KDV DAHİL gösterilir
                const qty = parseFloat(l.quantity) || 0;
                const netUnit = parseFloat(l.unit_price) || 0;  // backend için net
                const vatRate = parseFloat(l.vat_rate) || 0;
                const grossUnit = netUnit * (1 + vatRate / 100);
                const lineGrossTotal = qty * grossUnit;
                const ld1 = parseFloat(l.discount_rate1) || 0;
                const ld2 = parseFloat(l.discount_rate2) || 0;
                const ld3 = parseFloat(l.discount_rate3) || 0;
                const hasLineDisc = ld1 > 0 || ld2 > 0 || ld3 > 0;
                const lineTotal = lineGrossTotal * (1 - ld1 / 100) * (1 - ld2 / 100) * (1 - ld3 / 100);
                const active = activeLineIdx === i;
                return (
                  <div key={l.id}
                    onClick={() => setActiveLineIdx(i)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '32px 44px minmax(180px,1fr) 80px 80px 90px 44px',
                      padding: '10px 10px',
                      background: active ? 'rgba(245,158,11,0.15)' : 'transparent',
                      borderLeft: active ? '3px solid var(--warning)' : '3px solid transparent',
                      borderBottom: '1px solid var(--border-secondary)',
                      cursor: 'pointer',
                      fontSize: '13px',
                    }}>
                    <div style={{ fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{i + 1}</div>
                    {/* RESİM — hover'da fixed portal'da büyük önizleme */}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={(e) => {
                        if (!l.image_url) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoverImg({ url: l.image_url, x: rect.right + 12, y: rect.top, name: l.stock_name });
                      }}
                      onMouseLeave={() => setHoverImg(null)}
                      style={{ position: 'relative', width: 36, height: 36 }}
                    >
                      {l.image_url ? (
                        <img src={l.image_url} alt={l.stock_name}
                          style={{
                            width: 36, height: 36, objectFit: 'cover',
                            borderRadius: 4, border: '1px solid var(--border-secondary)',
                            background: '#fff', display: 'block', cursor: 'zoom-in',
                          }} />
                      ) : (
                        <div style={{
                          width: 36, height: 36, borderRadius: 4,
                          background: 'var(--bg-secondary)',
                          color: 'var(--text-tertiary)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', fontWeight: 700,
                          border: '1px solid var(--border-secondary)',
                        }}>{(l.stock_name || '?').substring(0, 2).toUpperCase()}</div>
                      )}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>{l.stock_name}</span>
                        {l.stock_code && (
                          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                            {l.stock_code}
                          </span>
                        )}
                        {l.barcode && (
                          <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                            · {l.barcode}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                        {colorSizeEnabled && (l.color_name || l.size_name) && (
                          <span style={{ color: '#6366f1', fontFamily: 'inherit', fontWeight: 600 }}>
                            {l.color_name && '🎨 ' + l.color_name}
                            {l.color_name && l.size_name && ' · '}
                            {l.size_name && '📏 ' + l.size_name}
                          </span>
                        )}
                        <span
                          onClick={(e) => { e.stopPropagation(); setActiveLineIdx(i); setPersonnelPickTarget(i); setShowPersonnelPick(true); }}
                          title={l.personnel_name ? 'Personeli değiştir' : 'Personel ata'}
                          style={{
                            marginLeft: 6, fontFamily: 'inherit', fontWeight: 600,
                            color: l.personnel_name ? 'var(--success)' : 'var(--text-tertiary)',
                            cursor: 'pointer',
                            textDecoration: 'underline dotted',
                          }}>
                          👨‍💼 {l.personnel_name || 'Personel ata'}
                        </span>
                        {hasLineDisc && (
                          <span
                            onClick={(e) => { e.stopPropagation(); setActiveLineIdx(i); setShowLineDiscount(true); }}
                            title="Satır iskontosunu düzenle"
                            style={{
                              marginLeft: 6, fontFamily: 'inherit', fontWeight: 700,
                              color: '#ec4899', cursor: 'pointer',
                              background: 'rgba(236,72,153,0.12)', padding: '1px 5px',
                              borderRadius: 4,
                            }}>
                            🏷️ {[ld1, ld2, ld3].filter(x => x > 0).map(x => '%' + fmt(x)).join(' + ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 600 }}>{fmt(qty)}</span>
                      <span style={{
                        marginLeft: 4, fontSize: '11px', fontWeight: 700,
                        color: 'var(--accent-primary)',
                        background: 'rgba(99,102,241,0.12)',
                        padding: '1px 5px', borderRadius: 4,
                        fontFamily: 'inherit',
                      }}>{l.unit_code || 'ad'}</span>
                    </div>
                    {(() => {
                      const foreignGrossUnit = l.foreign_currency
                        ? (parseFloat(l.foreign_unit_price) || 0) * (1 + vatRate / 100)
                        : null;
                      const foreignGrossTotal = foreignGrossUnit != null ? qty * foreignGrossUnit : null;
                      return (
                        <>
                          <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                            <div>{fmt(grossUnit)}</div>
                            {foreignGrossUnit != null && (
                              <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: 500 }}>
                                {fmt(foreignGrossUnit)} {curSym(l.foreign_currency)}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                            {hasLineDisc && (
                              <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textDecoration: 'line-through', fontWeight: 500 }}>
                                {fmt(lineGrossTotal)}
                              </div>
                            )}
                            <div style={{ color: hasLineDisc ? '#ec4899' : undefined }}>{fmt(lineTotal)}</div>
                            {foreignGrossTotal != null && (
                              <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: 500 }}>
                                {fmt(foreignGrossTotal)} {curSym(l.foreign_currency)}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                    <button
                      onClick={(e) => { e.stopPropagation(); removeLine(i); }}
                      title="Satırı sil"
                      style={{
                        background: 'transparent', border: 'none', color: 'var(--danger)',
                        cursor: 'pointer', fontSize: '22px', padding: 0, lineHeight: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >🗑</button>
                  </div>
                );
              })
            )}
              </div>
            </div>
          </div>

          {/* Sepet altı: barkod + miktar/fiyat butonları */}
          <div style={{
            padding: '8px 10px',
            background: 'var(--bg-secondary)',
            borderTop: '1px solid var(--border-secondary)',
            display: 'flex', gap: 6, alignItems: 'center',
          }}>
            <input
              ref={barcodeRef}
              type="text"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitBarcode(); }}
              placeholder="Barkod okutun · çoklu: 5*8690..."
              style={{
                flex: 1, padding: '10px 14px', fontSize: '14px',
                border: '2px solid var(--accent-primary)',
                borderRadius: 6, fontFamily: 'monospace',
                outline: 'none',
              }}
            />
            <button onClick={() => setShowItemSearch(true)} style={{
              padding: '10px 14px', border: '1px solid var(--border-secondary)',
              borderRadius: 6, background: 'var(--bg-card)', cursor: 'pointer', fontSize: '14px',
            }} title="Ürün ara">🔍</button>
          </div>
        </div>

        {/* SAĞ: SEKME PANELİ — TAB BAR ÜSTTE + İÇERİK ORTADA + ÖDEMEYE GEÇ ALTTA */}
        <div className="pos-rightpanel" style={{
          background: 'var(--bg-card)', borderRadius: 8,
          border: '1px solid var(--border-secondary)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          minHeight: 0,
        }}>
          {/* Resimli sekme bar */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 0,
            background: 'var(--bg-secondary)',
            borderBottom: '1px solid var(--border-secondary)',
          }}>
            {[
              { k: 'actions',     icon: '⚡', label: 'İşlemler',   color: '#6366f1' },
              { k: 'favorites',   icon: '⭐', label: 'Favoriler',  color: '#f59e0b' },
              { k: 'operational', icon: '⚙️', label: 'Operasyonel',color: '#10b981' },
            ].map(t => (
              <button key={t.k} onClick={() => setActiveTab(t.k)}
                style={{
                  padding: '10px 6px',
                  background: activeTab === t.k ? 'var(--bg-card)' : 'transparent',
                  borderTop: activeTab === t.k ? `3px solid ${t.color}` : '3px solid transparent',
                  borderBottom: 'none', borderLeft: 'none', borderRight: 'none',
                  color: activeTab === t.k ? t.color : 'var(--text-secondary)',
                  fontSize: '11px', fontWeight: activeTab === t.k ? 700 : 500,
                  cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                  transition: 'all 0.15s',
                }}>
                <span style={{ fontSize: '20px' }}>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* Sekme içeriği — scrollable */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {activeTab === 'actions' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Aksiyon butonları — 3 sütun grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                  {/* Satır 1: İşlem İptal | Müşteri | Personel */}
                  <button onClick={cancelTransaction} disabled={lines.length === 0} style={{
                    padding: '10px 8px', background: '#dc2626', color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: '12px', fontWeight: 700,
                    cursor: lines.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: lines.length === 0 ? 0.5 : 1,
                  }}>✕ İşlem İptal</button>
                  <button onClick={() => setShowCustomerPick(true)} style={{
                    padding: '10px 8px', background: '#6366f1', color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                  }}>👤 Müşteri</button>
                  <button onClick={() => { setPersonnelPickTarget(activeLineIdx); setShowPersonnelPick(true); }}
                    disabled={activeLineIdx == null}
                    style={{
                      padding: '10px 8px', background: '#10b981', color: '#fff',
                      border: 'none', borderRadius: 6, fontSize: '12px', fontWeight: 700,
                      cursor: activeLineIdx == null ? 'not-allowed' : 'pointer',
                      opacity: activeLineIdx == null ? 0.5 : 1,
                    }}>👨‍💼 Personel</button>

                  {/* Satır 2: Stok Durum | Yuvarlama */}
                  <button onClick={() => setShowStockStatus(true)} style={{
                    padding: '10px 8px', background: '#0ea5e9', color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                  }}>📦 Stok Durum</button>
                  {roundingEnabled && (
                    <button onClick={() => setShowRounding(true)}
                      disabled={lines.length === 0}
                      style={{
                        padding: '10px 8px', background: '#0891b2', color: '#fff',
                        border: 'none', borderRadius: 6, fontSize: '12px', fontWeight: 700,
                        cursor: lines.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: lines.length === 0 ? 0.5 : 1,
                      }}>⊕ Yuvarlama</button>
                  )}
                  <button onClick={() => requestTierChange(priceTier === 1 ? 3 : 1)}
                    title={priceTier === 1 ? 'Vadeli fiyata geç' : 'Peşin fiyata geç'}
                    style={{
                      padding: '10px 8px',
                      background: priceTier === 1 ? '#16a34a' : '#a16207',
                      color: '#fff', border: 'none', borderRadius: 6,
                      fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    }}>
                    💰 {priceTier === 1 ? 'Peşin' : 'Vadeli'} → {priceTier === 1 ? 'Vadeli' : 'Peşin'}
                  </button>

                  {/* Satır 3: Satır İskonto | Satışı Beklet | Bekleyen Satışlar */}
                  <button onClick={() => { if (activeLineIdx == null) { setMsg({ type: 'error', text: 'Önce sepetten satır seçin' }); setTimeout(() => setMsg(null), 2000); return; } setShowLineDiscount(true); }}
                    disabled={activeLineIdx == null || discountLevels === 0}
                    title={discountLevels === 0 ? 'Ayarlar → Perakende Satış → İskonto Sayısı 0' : ''}
                    style={{
                      padding: '10px 8px', background: '#ec4899', color: '#fff',
                      border: 'none', borderRadius: 6, fontSize: '12px', fontWeight: 700,
                      cursor: (activeLineIdx == null || discountLevels === 0) ? 'not-allowed' : 'pointer',
                      opacity: (activeLineIdx == null || discountLevels === 0) ? 0.5 : 1,
                    }}>🏷️ Satır İskonto</button>
                  <button onClick={parkCurrent}
                    disabled={lines.length === 0}
                    style={{
                      padding: '10px 8px', background: '#7c3aed', color: '#fff',
                      border: 'none', borderRadius: 6, fontSize: '12px', fontWeight: 700,
                      cursor: lines.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: lines.length === 0 ? 0.5 : 1,
                    }}>⏸️ Satışı Beklet</button>
                  <button onClick={() => setShowParked(true)}
                    style={{
                      padding: '10px 8px',
                      background: parkedSales.length > 0 ? '#a855f7' : 'var(--bg-secondary)',
                      color: parkedSales.length > 0 ? '#fff' : 'var(--text-tertiary)',
                      border: parkedSales.length > 0 ? 'none' : '1px solid var(--border-secondary)',
                      borderRadius: 6, fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}>
                    ↺ Bekleyenler
                    {parkedSales.length > 0 && (
                      <span style={{
                        background: 'rgba(255,255,255,0.3)', color: '#fff',
                        padding: '1px 6px', borderRadius: 'var(--r-full)', fontSize: '10px', fontWeight: 800,
                      }}>{parkedSales.length}</span>
                    )}
                  </button>
                </div>

                {/* Edit mode rozeti + buffer */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div style={{ padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '11px' }}>
                    <div style={{ color: 'var(--text-tertiary)' }}>Düzenleme</div>
                    <div style={{ fontWeight: 700 }}>
                      {editMode === 'barcode' ? '🔍 Barkod (Ara)'
                        : editMode === 'quantity' ? '📊 Miktar'
                        : editMode === 'price' ? '💰 Fiyat'
                        : editMode}
                    </div>
                  </div>
                  <div style={{ padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '11px' }}>
                    <div style={{ color: 'var(--text-tertiary)' }}>Giriş</div>
                    <div style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                      {editMode === 'barcode' ? (barcodeInput || '—') : (numericBuffer || '—')}
                    </div>
                  </div>
                </div>

                {/* Sayısal tuş takımı 4×4 */}
                <Keypad
                  onPress={numericPress}
                  keys={[
                    { k: '1' }, { k: '2' }, { k: '3' },
                    { k: 'qty', label: '📊\nMiktar', color: '#f59e0b', textColor: '#fff', action: () => { if (activeLineIdx != null) setEditMode('quantity'); } },
                    { k: '4' }, { k: '5' }, { k: '6' },
                    { k: 'price', label: '💰\nFiyat', color: user?.retailAllowPriceEdit === false ? '#94a3b8' : '#10b981', textColor: '#fff',
                      action: () => {
                        if (user?.retailAllowPriceEdit === false) {
                          setMsg({ type: 'error', text: 'Manuel fiyat değiştirme yetkiniz yok. Yöneticinize başvurun.' });
                          setTimeout(() => setMsg(null), 3000);
                          return;
                        }
                        if (activeLineIdx != null) setEditMode('price');
                      } },
                    { k: '7' }, { k: '8' }, { k: '9' },
                    { k: 'bcode', label: '🔍\nBarkod', color: '#3b82f6', textColor: '#fff', action: () => { setEditMode('barcode'); barcodeRef.current?.focus(); } },
                    { k: 'clear', label: '⌫\nSil', color: '#ef4444', textColor: '#fff', action: () => numericPress('←') },
                    { k: '0' }, { k: '.' },
                    { k: 'end', label: editMode === 'barcode' ? '⏎\nAra' : '✓\nUygula', color: 'var(--success)', textColor: '#fff', action: applyOrSearch },
                  ]}
                />

              </div>
            )}

            {activeTab === 'favorites' && (() => {
              const categories = Array.from(new Set(favorites.map(f => f.category_id).filter(Boolean)))
                .map(cid => ({ id: cid, name: favorites.find(f => f.category_id === cid)?.category_name || 'Kategorisiz' }));
              const noCatExists = favorites.some(f => !f.category_id);
              const filteredFavs = activeCategory === 'all'
                ? favorites
                : activeCategory === 'none'
                  ? favorites.filter(f => !f.category_id)
                  : favorites.filter(f => f.category_id === activeCategory);

              if (favorites.length === 0) {
                return (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                    ⭐ Henüz favori ürün yok. Stok kartında "POS Favorisi" alanını "Favori" yapın.
                  </div>
                );
              }

              return (
                <>
                  {(categories.length > 0 || noCatExists) && (
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto', flexWrap: 'wrap', paddingBottom: 2 }}>
                      {categories.map((c, i) => {
                        const count = favorites.filter(f => f.category_id === c.id).length;
                        const active = activeCategory === c.id;
                        const color = FAV_COLORS[i % FAV_COLORS.length];
                        return (
                          <button key={c.id}
                            onClick={() => setActiveCategory(active ? 'all' : c.id)}
                            style={catChip(active, color)}>
                            <span>{c.name}</span>
                            <span style={catChipBadge(active, color)}>{count}</span>
                          </button>
                        );
                      })}
                      {noCatExists && (() => {
                        const active = activeCategory === 'none';
                        const color = '#64748b';
                        return (
                          <button onClick={() => setActiveCategory(active ? 'all' : 'none')}
                            style={catChip(active, color)}>
                            <span>Kategorisiz</span>
                            <span style={catChipBadge(active, color)}>{favorites.filter(f => !f.category_id).length}</span>
                          </button>
                        );
                      })()}
                    </div>
                  )}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 6,
                  }}>
                    {filteredFavs.map((s, i) => {
                      const tileColor = FAV_COLORS[i % FAV_COLORS.length];
                      return (
                        <button
                          key={s.id}
                          onClick={() => s.is_weighted ? setWeighedPick(s) : addLine(s)}
                          style={{
                            background: 'var(--bg-card)',
                            color: 'var(--text-primary)',
                            border: `2px solid ${tileColor}`,
                            borderRadius: 8,
                            padding: 0,
                            minHeight: 120,
                            overflow: 'hidden',
                            cursor: 'pointer', textAlign: 'left',
                            display: 'flex', flexDirection: 'column',
                            fontSize: '11px',
                            transition: 'transform 0.1s, box-shadow 0.15s',
                          }}
                          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.96)'}
                          onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          onMouseEnter={(e) => e.currentTarget.style.boxShadow = `0 4px 12px ${tileColor}55`}
                        >
                          {/* Üstte resim alanı — buton içinde tam görünür, contain ile */}
                          <div style={{
                            width: '100%', height: 60,
                            background: s.image_url ? '#fff' : tileColor,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            overflow: 'hidden',
                            borderBottom: `1px solid ${tileColor}`,
                            position: 'relative',
                          }}>
                            {s.image_url ? (
                              <img src={s.image_url} alt={s.name}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            ) : (
                              <span style={{ fontSize: '20px', color: '#fff', fontWeight: 800, opacity: 0.85 }}>
                                {(s.name || '?').substring(0, 2).toUpperCase()}
                              </span>
                            )}
                            {s.is_weighted && (
                              <span title="Tartılı ürün — tıklayınca miktar sorar" style={{
                                position: 'absolute', top: 2, right: 2,
                                background: 'var(--warning)', color: '#fff',
                                fontSize: 9, padding: '1px 5px',
                                borderRadius: 'var(--r-full)', fontWeight: 700,
                              }}>⚖</span>
                            )}
                          </div>
                          {/* Bilgi alanı */}
                          <div style={{ padding: '6px 8px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 3 }}>
                            <div style={{ lineHeight: 1.2, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                              {s.name}
                            </div>
                            {/* Kategori + Renk + Beden rozetleri */}
                            {(s.category_name || (colorSizeEnabled && (s.color_name || s.size_name))) && (
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {s.category_name && (
                                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 'var(--r-full)', background: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 700 }}>📁 {s.category_name}</span>
                                )}
                                {colorSizeEnabled && s.color_name && (
                                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 'var(--r-full)', background: 'rgba(99,102,241,0.15)', color: '#6366f1', fontWeight: 700 }}>🎨 {s.color_name}</span>
                                )}
                                {colorSizeEnabled && s.size_name && (
                                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 'var(--r-full)', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 700 }}>📏 {s.size_name}</span>
                                )}
                              </div>
                            )}
                            <div style={{ fontSize: '13px', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', textAlign: 'right', color: tileColor }}>
                              {fmt(resolvePrice(s))} {curSym(s.pd_currency_code || priceDef?.currency_code)}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            {activeTab === 'operational' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button style={opBtn}>🖨️ Z Raporu</button>
                <button style={opBtn}>📊 X Raporu</button>
                <button style={opBtn}>💵 Çekmece Aç</button>
                <button style={opBtn}>📥 Kasa Giriş</button>
                <button style={opBtn}>📤 Kasa Çıkış</button>
                <button style={opBtn}>🔚 Vardiya Kapat</button>
                <button
                  style={{ ...opBtn, borderColor: '#f59e0b', color: '#f59e0b' }}
                  disabled={!activeTerminalId}
                  onClick={() => setShowOkcChecks(true)}
                  title={activeTerminalId ? 'Kasadaki açık çekleri listele' : 'Önce üst barda kasa seçin'}
                >🧾 OKC İşlemler (Açık Çekler)</button>
              </div>
            )}
          </div>

          {/* ÖDEMEYE GEÇ — sağ panelin en altında sabit */}
          <button onClick={openPayment} disabled={lines.length === 0} style={{
            padding: '18px 12px', background: 'var(--success)', color: '#fff',
            border: 'none', borderRadius: 0,
            fontSize: '17px', fontWeight: 800,
            cursor: lines.length === 0 ? 'not-allowed' : 'pointer',
            opacity: lines.length === 0 ? 0.5 : 1,
            borderTop: '1px solid rgba(0,0,0,0.1)',
          }}>✓ ÖDEMEYE GEÇ</button>
        </div>
      </div>

      {/* ─── MESAJ TOAST ─── */}
      {msg && (
        <div
          onClick={() => setMsg(null)}
          title="Kapatmak için tıklayın"
          style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            padding: '10px 16px 10px 20px', borderRadius: 8,
            background: msg.type === 'error' ? '#ef4444' : msg.type === 'success' ? '#10b981' : '#3b82f6',
            color: '#fff', fontWeight: 600, zIndex: 1000,
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'flex-start', gap: 12,
            maxWidth: '90vw',
            whiteSpace: 'pre-line',
          }}>
          <span style={{ flex: 1 }}>{msg.text}</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMsg(null); }}
            aria-label="Kapat"
            style={{
              background: 'transparent', border: 'none', color: '#fff',
              fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 0, marginTop: 1,
            }}
          >✕</button>
        </div>
      )}

      {/* ─── YN ÖKC POS Overlay (Protel kasa akışı) ─── */}
      {posOverlay && (() => {
        const isError = posOverlay.stage === 'error';
        const isDone  = posOverlay.stage === 'done';
        const bgColor = isError ? 'rgba(127,29,29,0.92)' : isDone ? 'rgba(6,78,59,0.92)' : 'rgba(0,0,0,0.85)';
        return (
          <div style={{
            position: 'fixed', inset: 0, background: bgColor, zIndex: 3000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            color: '#fff',
          }}>
            {!isError && !isDone && (
              <div style={{ fontSize: 64, marginBottom: 20, animation: 'pulse 1.5s infinite' }}>
                {posOverlay.hasCard ? '💳' : '🧾'}
              </div>
            )}
            <div style={{
              fontSize: isError ? 22 : 26, fontWeight: 700, marginBottom: 12,
              textAlign: 'center', padding: '0 30px', whiteSpace: 'pre-line', lineHeight: 1.4,
            }}>
              {posOverlay.text}
            </div>
            {!isError && !isDone && (
              <div style={{ marginTop: 24, width: 320, height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#10b981', animation: 'progressIndefinite 2s ease-in-out infinite' }} />
              </div>
            )}
            {isError && (
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 8 }}>
                5 saniye içinde otomatik kapanır...
              </div>
            )}
            {posOverlay.check_seq && !isError && !isDone && (
              <button onClick={async () => {
                if (!window.confirm('Kasada iptal yapıldı mı onayla? Çek iptal edilecek ve satış kaydedilmeyecek.')) return;
                try {
                  await api.post('/pos-terminals/' + activeTerminalId + '/manual-withdraw', { check_seq: posOverlay.check_seq });
                } catch (e) { /* withdraw zaten arka planda denenir, hata kritik değil */ }
                setPosOverlay(null);
                setSaving(false);
              }} style={{
                marginTop: 30, padding: '12px 28px', fontSize: 14, fontWeight: 700,
                background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
              }}>🛑 Manuel İptal Et (kasada iptal ettiyseniz)</button>
            )}
            {isError && (
              <button onClick={() => setPosOverlay(null)} style={{
                marginTop: 16, padding: '8px 20px', fontSize: 13,
                background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 6, cursor: 'pointer',
              }}>Kapat</button>
            )}
          </div>
        );
      })()}

      {/* ─── OKC AÇIK ÇEKLER MODAL ─── */}
      {showOkcChecks && (() => {
        const activeTerm = posTerminals.find(t => t.id === activeTerminalId);
        const loadChecks = async () => {
          setOkcChecks({ loading: true, items: [], err: '' });
          try {
            const r = await api.get('/pos-terminals/' + activeTerminalId + '/open-checks');
            const items = r.data?.data?.items || [];
            setOkcChecks({ loading: false, items, err: '' });
          } catch (e) {
            setOkcChecks({ loading: false, items: [], err: e.response?.data?.message || e.message });
          }
        };
        const withdrawOne = async (checkSeq) => {
          if (!window.confirm(`#${checkSeq} numaralı açık çek kasadan silinecek. Devam edilsin mi?`)) return;
          try {
            await api.post('/pos-terminals/' + activeTerminalId + '/manual-withdraw', { check_seq: checkSeq });
            setMsg({ type: 'success', text: `Çek #${checkSeq} silindi` });
            await loadChecks();
          } catch (e) {
            setMsg({ type: 'error', text: 'Silme hatası: ' + (e.response?.data?.message || e.message) });
          }
        };
        return (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2500,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} onClick={() => setShowOkcChecks(false)}>
            <div style={{
              background: 'var(--bg-card)', borderRadius: 12, width: 720, maxWidth: '94vw',
              maxHeight: '86vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{
                padding: '14px 20px', borderBottom: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>🧾 OKC İşlemler — Açık Çekler</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Kasa: <strong>{activeTerm?.name || activeTerm?.code || '—'}</strong>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={loadChecks} disabled={okcChecks.loading} style={{
                    padding: '8px 14px', background: 'var(--primary)', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
                  }}>🔄 {okcChecks.loading ? 'Yükleniyor...' : 'Yenile'}</button>
                  <button onClick={() => setShowOkcChecks(false)} style={{
                    padding: '8px 14px', background: 'transparent', color: 'var(--text-secondary)',
                    border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
                  }}>Kapat</button>
                </div>
              </div>
              <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
                {okcChecks.err && (
                  <div style={{
                    padding: 12, background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                    borderRadius: 6, marginBottom: 12, fontSize: 13,
                  }}>❌ {okcChecks.err}</div>
                )}
                {!okcChecks.loading && !okcChecks.err && okcChecks.items.length === 0 && (
                  <div style={{
                    padding: '40px 20px', textAlign: 'center', color: 'var(--text-secondary)',
                  }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
                    <div>Liste yüklemek için "Yenile" tıklayın</div>
                    <div style={{ fontSize: 12, marginTop: 6 }}>
                      Kasadaki açık (kapanmamış) çekler listelenecek
                    </div>
                  </div>
                )}
                {okcChecks.items.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                        <th style={{ padding: 10 }}>Check Seq</th>
                        <th style={{ padding: 10 }}>Status</th>
                        <th style={{ padding: 10, textAlign: 'right' }}>Tutar</th>
                        <th style={{ padding: 10 }}>Tarih</th>
                        <th style={{ padding: 10, textAlign: 'center' }}>İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {okcChecks.items.map((c, idx) => {
                        const seq = c.checkSeq || c.CheckSeq || c.seq || c.id;
                        const status = c.checkStatus || c.CheckStatus || c.status || '—';
                        const total = (c.checkTotal || c.CheckTotal || c.total || 0) / 100;
                        const date = c.businessDate || c.BusinessDate || c.openDate || c.OpenDate || '';
                        return (
                          <tr key={seq || idx} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: 10, fontFamily: 'monospace', fontWeight: 600 }}>#{seq}</td>
                            <td style={{ padding: 10 }}>
                              <span style={{
                                padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                                background: 'rgba(245,158,11,0.15)', color: '#f59e0b',
                              }}>{status}</span>
                            </td>
                            <td style={{ padding: 10, textAlign: 'right', fontWeight: 600 }}>
                              {total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                            </td>
                            <td style={{ padding: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
                              {date ? new Date(date).toLocaleString('tr-TR') : '—'}
                            </td>
                            <td style={{ padding: 10, textAlign: 'center' }}>
                              <button onClick={() => withdrawOne(seq)} style={{
                                padding: '5px 12px', background: 'var(--danger)', color: '#fff',
                                border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                              }}>🗑️ Sil</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div style={{
                padding: '10px 20px', borderTop: '1px solid var(--border)',
                fontSize: 12, color: 'var(--text-secondary)',
              }}>
                ⚠ "Sil" → kasada `withdraw` çağırır. Bağlı satış/fatura ERP tarafında yoksa sadece kasa fişi silinir.
              </div>
            </div>
          </div>
        );
      })()}

      {/* ─── BİLGİ FİŞİ ONAY MODAL (kasa yok + e-fatura yok) ─── */}
      {infoSlipPrompt && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2600,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: 12, width: 520, maxWidth: '94vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)', padding: 24,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 36 }}>🧾</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>Bilgi Fişi Yazdır</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                  Kasa terminali seçili değil ve e-Fatura ayarları eksik
                </div>
              </div>
            </div>
            <div style={{
              padding: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 6, fontSize: 13, lineHeight: 1.6, marginBottom: 16,
            }}>
              <strong>Bu satış için e-Belge kesilemiyor.</strong> Eksik:
              <ul style={{ margin: '6px 0 0 20px', padding: 0 }}>
                {(infoSlipPrompt.missing || []).map(m => <li key={m}>{m}</li>)}
              </ul>
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                Yine de satışı kaydedebilir, terminal yazıcıdan bilgi fişi basabilirsiniz.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setInfoSlipPrompt(null); }} style={{
                padding: '10px 16px', background: 'transparent', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
              }}>İptal</button>
              <button onClick={async () => {
                const splits = infoSlipPrompt.splits;
                setInfoSlipPrompt(null);
                // Yazdırma yapma, sadece sales_invoices kaydet
                await completePayment(splits, { skipDispatchMode: true, infoSlipPrinted: false });
              }} style={{
                padding: '10px 16px', background: 'var(--bg-secondary)', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
              }}>Yazdırma, Sadece Kaydet</button>
              <button onClick={async () => {
                const splits = infoSlipPrompt.splits;
                const taxpayer = infoSlipPrompt.taxpayer;
                setInfoSlipPrompt(null);
                // Önce kaydı yap (invoice_number için), sonra fişi aç
                await completePayment(splits, { skipDispatchMode: true, infoSlipPrint: true, infoSlipTaxpayer: taxpayer });
              }} style={{
                padding: '10px 18px', background: 'var(--success)', color: '#fff',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700,
              }}>🖨️ Yazdır ve Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ÜRÜN ARAMA MODAL ─── */}
      {showItemSearch && (
        <ItemSearchModal
          priceDefId={priceDef?.id}
          priceTier={priceTier}
          colorSizeEnabled={colorSizeEnabled}
          onPick={(stock, qty) => { addLine(stock, qty || 1); }}
          onClose={() => { setShowItemSearch(false); barcodeRef.current?.focus(); }}
        />
      )}

      {/* ─── MÜŞTERİ SEÇİM MODAL ─── */}
      {showCustomerPick && (() => {
        const _bid = appSettings?.retail_default_branch_id || user?.defaultBranchId || null;
        const _bname = branches.find(b => b.id === _bid)?.name || null;
        return (
          <CustomerPickModal
            branchId={_bid}
            branchName={_bname}
            onPick={(c) => { setCustomer(c); setShowCustomerPick(false); }}
            onClose={() => setShowCustomerPick(false)}
          />
        );
      })()}

      {/* ─── PERSONEL SEÇİM MODAL ─── */}
      {showPersonnelPick && (
        <PersonnelPickModal
          personnel={personnel}
          active={personnelPickTarget != null ? (lines[personnelPickTarget] && { id: lines[personnelPickTarget].personnel_id, full_name: lines[personnelPickTarget].personnel_name }) : activePersonnel}
          target={personnelPickTarget != null ? `Satır ${personnelPickTarget + 1} — ${lines[personnelPickTarget]?.stock_name || ''}` : 'Sonraki satırlar için varsayılan'}
          onPick={(p) => {
            if (personnelPickTarget != null && lines[personnelPickTarget]) {
              // Belirli bir satırın personelini güncelle
              updateLine(personnelPickTarget, {
                personnel_id: p?.id || null,
                personnel_name: p?.full_name || null,
              });
            } else {
              // Üst bardaki default — bundan sonra eklenen satırlara atanır
              setActivePersonnel(p);
            }
            setShowPersonnelPick(false);
            setPersonnelPickTarget(null);
          }}
          onClose={() => { setShowPersonnelPick(false); setPersonnelPickTarget(null); }}
        />
      )}

      {/* ─── STOK DURUM MODAL ─── */}
      {showStockStatus && (
        <StockStatusModal colorSizeEnabled={colorSizeEnabled} onClose={() => setShowStockStatus(false)} />
      )}

      {/* ─── BEKLEYEN SATIŞLAR MODAL ─── */}
      {showParked && (
        <div onClick={() => setShowParked(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: 10, width: 'min(640px, 95vw)',
            maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>↺ Bekleyen Satışlar</span>
              <span style={{ marginLeft: 8, padding: '2px 10px', background: 'rgba(124,58,237,0.15)', color: '#7c3aed', borderRadius: 'var(--r-full)', fontSize: '12px', fontWeight: 700 }}>
                {parkedSales.length}
              </span>
              <button onClick={() => setShowParked(false)} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {parkedSales.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📭</div>
                  <div>Bekletilen satış yok</div>
                  <div style={{ fontSize: '12px', marginTop: 6 }}>Sepette ürün varken "⏸️ Satışı Beklet" ile saklayabilirsiniz</div>
                </div>
              ) : (
                parkedSales.map(p => (
                  <div key={p.id} style={{
                    padding: 12, marginBottom: 6,
                    border: '1px solid var(--border-secondary)', borderRadius: 8,
                    background: 'var(--bg-secondary)',
                    display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontWeight: 700 }}>⏸️ {p.label}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                        {new Date(p.savedAt).toLocaleString('tr-TR')} · {(p.lines || []).length} kalem
                        {p.customer && <> · 👤 {p.customer.title || p.customer.name}</>}
                        {p.activePersonnel && <> · 👨‍💼 {p.activePersonnel.full_name}</>}
                      </div>
                      <div style={{ fontSize: '15px', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: 'var(--accent-primary)', marginTop: 2 }}>
                        {fmt(p.grandTotal || 0)} {curSym(priceDef?.currency_code)}
                      </div>
                    </div>
                    <button onClick={() => restoreParked(p)} style={{
                      padding: '10px 16px', background: 'var(--success)', color: '#fff',
                      border: 'none', borderRadius: 6, fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                    }}>↻ Yükle</button>
                    <button onClick={() => deleteParked(p.id)} style={{
                      padding: '10px 12px', background: 'transparent', color: 'var(--danger)',
                      border: '1px solid var(--danger)', borderRadius: 6, fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                    }}>🗑</button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── İSKONTO MODAL ─── */}
      {showDiscount && (
        <DiscountModal
          levels={discountLevels}
          value={headerDisc}
          totals={totals}
          onApply={(v) => { setHeaderDisc(v); setShowDiscount(false); }}
          onClose={() => setShowDiscount(false)}
        />
      )}

      {/* ─── YUVARLAMA MODAL ─── */}
      {showRounding && (
        <RoundingModal
          value={headerDisc.rounding}
          totals={totals}
          onApply={(rounding) => { setHeaderDisc(p => ({ ...p, rounding })); setShowRounding(false); }}
          onClose={() => setShowRounding(false)}
        />
      )}

      {/* ─── SATIR İSKONTO MODAL ─── */}
      {showLineDiscount && activeLineIdx != null && lines[activeLineIdx] && (
        <LineDiscountModal
          line={lines[activeLineIdx]}
          levels={discountLevels}
          onApply={(rates) => { updateLine(activeLineIdx, rates); setShowLineDiscount(false); }}
          onClose={() => setShowLineDiscount(false)}
        />
      )}

      {/* ─── FİYAT TİPİ DEĞİŞTİRME ONAYI ─── */}
      {showTierChangeConfirm && (
        <div onClick={() => setShowTierChangeConfirm(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: 'var(--bg-card)', borderRadius: 10, width: 'min(420px, 95vw)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: 16, borderBottom: '1px solid var(--border-secondary)' }}>
              <strong style={{ fontSize: '1.1rem' }}>💰 Fiyat Tipi Değişti</strong>
            </div>
            <div style={{ padding: 16, fontSize: '13px', lineHeight: 1.5 }}>
              <div><strong>{showTierChangeConfirm.from === 1 ? 'Peşin' : 'Vadeli'}</strong> → <strong>{showTierChangeConfirm.to === 1 ? 'Peşin' : 'Vadeli'}</strong></div>
              <div style={{ marginTop: 10, color: 'var(--text-secondary)' }}>
                Sepette {lines.length} ürün var. Bu ürünlerin fiyatları yeni tipe göre güncellensin mi?
              </div>
              <div style={{ marginTop: 8, fontSize: '11px', color: 'var(--text-tertiary)' }}>
                • <strong>Evet</strong>: Mevcut ürünlerin fiyatları yeniden hesaplanır (satır iskontoları korunur)<br/>
                • <strong>Hayır</strong>: Mevcut ürünler aynı kalır, sadece sonradan eklenenler yeni fiyat tipini alır
              </div>
            </div>
            <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)', display: 'flex', gap: 8 }}>
              <button onClick={() => setShowTierChangeConfirm(null)} style={{
                padding: '10px 14px', background: 'var(--bg-secondary)',
                border: '1px solid var(--border-secondary)', borderRadius: 6,
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              }}>Vazgeç</button>
              <button onClick={() => applyTierChange(showTierChangeConfirm.to, false)} style={{
                flex: 1, padding: '10px 14px', background: 'var(--bg-secondary)',
                border: '1px solid var(--border-secondary)', borderRadius: 6,
                fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              }}>Hayır (sadece sonrakiler)</button>
              <button onClick={() => applyTierChange(showTierChangeConfirm.to, true)} style={{
                flex: 1, padding: '10px 14px', background: 'var(--success)', color: '#fff',
                border: 'none', borderRadius: 6,
                fontSize: '13px', fontWeight: 800, cursor: 'pointer',
              }}>Evet, güncelle</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── RESİM HOVER ÖNİZLEME (fixed — clipping'i aşar) ─── */}
      {hoverImg && (() => {
        // Konumu sınırla — viewport dışına taşmasın
        const SIZE = 280;
        const x = Math.min(hoverImg.x, window.innerWidth - SIZE - 16);
        const y = Math.min(Math.max(hoverImg.y - 50, 16), window.innerHeight - SIZE - 16);
        return (
          <div style={{
            position: 'fixed', left: x, top: y, zIndex: 3000,
            pointerEvents: 'none',
            background: '#fff', borderRadius: 8,
            border: '2px solid var(--accent-primary)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
            padding: 6,
          }}>
            <img src={hoverImg.url} alt={hoverImg.name}
              style={{ width: SIZE - 12, height: SIZE - 12, objectFit: 'contain', display: 'block' }} />
            <div style={{
              position: 'absolute', bottom: 6, left: 6, right: 6,
              background: 'rgba(0,0,0,0.7)', color: '#fff',
              padding: '4px 8px', borderRadius: 4,
              fontSize: '12px', fontWeight: 700, textAlign: 'center',
            }}>{hoverImg.name}</div>
          </div>
        );
      })()}

      {/* ─── TARTI MİKTAR MODAL ─── */}
      {weighedPick && (
        <WeightedQtyModal
          stock={weighedPick}
          priceTier={priceTier}
          onConfirm={(qty) => { addLine(weighedPick, qty || 1); setWeighedPick(null); }}
          onClose={() => setWeighedPick(null)}
        />
      )}

      {/* ─── ÖDEME MODAL ─── */}
      {showPayment && (
        <PaymentModal
          total={totals.grandTotal}
          customer={customer || defaultCustomer}
          hasCashRegister={!!cashRegisterId}
          hasBankAccount={!!bankAccountId}
          bankAccounts={bankAccounts}
          posDefinitions={posDefinitions}
          availableVouchers={availableVouchers}
          permissions={{
            allowCreditCard:   user?.retailAllowCreditCard   !== false,
            allowBankTransfer: user?.retailAllowBankTransfer !== false,
            // Alışveriş çeki: kullanıcı yetkisi AND global ayar (shopping_voucher_enabled)
            allowVoucher:      (user?.retailAllowVoucher !== false)
                                && String(appSettings?.shopping_voucher_enabled || 'false').toLowerCase() === 'true',
            allowOnAccount:    user?.retailAllowOnAccount    !== false,
          }}
          onComplete={completePayment}
          onClose={() => setShowPayment(false)}
          saving={saving}
        />
      )}
    </div>
  );
}

const opBtn = {
  padding: '10px 14px', minHeight: 44,
  background: 'var(--bg-card)', border: '1px solid var(--border-secondary)',
  borderRadius: 6, fontSize: '12px', fontWeight: 600, cursor: 'pointer',
};

const catChip = (active, color) => ({
  padding: '7px 14px',
  background: active ? color : 'var(--bg-card)',
  color: active ? '#fff' : 'var(--text-primary)',
  border: '1.5px solid ' + (active ? color : 'var(--border-secondary)'),
  borderRadius: 'var(--r-lg)',
  fontSize: '12px',
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center', gap: 8,
  boxShadow: active ? `0 3px 10px ${color}55` : 'none',
  transition: 'transform 0.1s, box-shadow 0.15s',
});

const catChipBadge = (active, color) => ({
  background: active ? 'rgba(255,255,255,0.25)' : `${color}22`,
  color: active ? '#fff' : color,
  fontSize: '10px',
  padding: '2px 8px',
  borderRadius: 'var(--r-full)',
  fontWeight: 800,
  fontFamily: 'JetBrains Mono, monospace',
  minWidth: 18, textAlign: 'center',
});

// ── Ürün arama modal'ı (çoklu seçimli — fatura tarzı) ─────────────
function ItemSearchModal({ priceDefId, priceTier = 1, colorSizeEnabled = false, onPick, onClose }) {
  const tierPrice = (s) => {
    const v = s['pd_price' + priceTier];
    if (v != null && parseFloat(v) > 0) return parseFloat(v);
    return parseFloat(s.pd_price1) || 0;
  };
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allStocksWithPrice, setAllStocksWithPrice] = useState([]);
  const [selected, setSelected] = useState({});  // { stock_id: qty }
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    if (priceDefId) {
      api.get('/sales-invoices/lookups/all?retail_only=true&price_definition_id=' + priceDefId)
        .then(r => { if (r.data?.data?.stocks) setAllStocksWithPrice(r.data.data.stocks); })
        .catch(() => {});
    }
  }, [priceDefId]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      const ql = q.toLowerCase().trim();
      let list = allStocksWithPrice;
      if (ql.length >= 2) {
        list = list.filter(s =>
          (s.code || '').toLowerCase().includes(ql) ||
          (s.name || '').toLowerCase().includes(ql) ||
          (s.barcode || '').toLowerCase().includes(ql)
        );
      } else {
        list = list.slice(0, 80);  // arama yoksa ilk 80
      }
      setResults(list.slice(0, 100));
      setLoading(false);
    }, 150);
    return () => clearTimeout(timer);
  }, [q, allStocksWithPrice]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[id] != null) delete next[id];
      else next[id] = 1;
      return next;
    });
  };

  const updateQty = (id, val) => {
    const num = parseFloat(val);
    setSelected(prev => ({ ...prev, [id]: (isNaN(num) || num <= 0) ? 1 : num }));
  };

  const selectedCount = Object.keys(selected).length;
  const selectedTotal = results.reduce((acc, s) => {
    const q = selected[s.id];
    if (q == null) return acc;
    return acc + q * tierPrice(s);
  }, 0);

  const applyAll = () => {
    // Seçili tüm ürünleri sırayla ekle
    const list = allStocksWithPrice.filter(s => selected[s.id] != null);
    list.forEach(s => onPick(s, selected[s.id]));
    onClose();
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 10, width: 'min(820px, 95vw)',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>🔍 Ürün Bul (Çoklu Seçim)</span>
          {selectedCount > 0 && (
            <span style={{ background: 'var(--accent-primary)', color: '#fff', padding: '2px 10px', borderRadius: 'var(--r-full)', fontSize: '12px', fontWeight: 700 }}>
              {selectedCount} seçildi · {fmt(selectedTotal)} {curSym(results[0]?.pd_currency_code)}
            </span>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 12 }}>
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Kod, ad veya barkod yazın... (boş bırakırsanız ilk 80 ürün gelir)"
            style={{ width: '100%', padding: '14px 16px', fontSize: '16px',
              border: '2px solid var(--accent-primary)', borderRadius: 8, outline: 'none' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
          {loading && <div style={{ textAlign: 'center', padding: 20 }}>Yükleniyor...</div>}
          {!loading && results.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-tertiary)' }}>Sonuç yok</div>
          )}
          {results.map(s => {
            const isSelected = selected[s.id] != null;
            return (
              <div key={s.id}
                onClick={() => toggle(s.id)}
                style={{
                  display: 'grid', gridTemplateColumns: '32px 1fr auto auto', gap: 12, padding: '8px 10px',
                  background: isSelected ? 'rgba(99,102,241,0.12)' : 'var(--bg-secondary)',
                  marginBottom: 4,
                  border: '1px solid ' + (isSelected ? 'var(--accent-primary)' : 'var(--border-secondary)'),
                  borderRadius: 6, cursor: 'pointer', alignItems: 'center',
                }}>
                <input type="checkbox" checked={isSelected} readOnly
                  style={{ width: 20, height: 20, cursor: 'pointer' }} />
                <div onClick={(e) => e.stopPropagation()}>
                  <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{s.name}</span>
                    {s.category_name && (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 700 }}>📁 {s.category_name}</span>
                    )}
                    {colorSizeEnabled && s.color_name && (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(99,102,241,0.15)', color: '#6366f1', fontWeight: 700 }}>🎨 {s.color_name}</span>
                    )}
                    {colorSizeEnabled && s.size_name && (
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 700 }}>📏 {s.size_name}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                    {s.code}{s.barcode ? ' · ' + s.barcode : ''}{s.unit_code ? ' · ' + s.unit_code : ''}
                  </div>
                </div>
                {isSelected ? (
                  <input type="number" min="0" step="any" value={selected[s.id]}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateQty(s.id, e.target.value)}
                    style={{
                      width: 80, padding: '6px 8px', fontSize: '14px',
                      border: '1px solid var(--accent-primary)', borderRadius: 4,
                      fontFamily: 'monospace', textAlign: 'right', fontWeight: 700,
                    }} />
                ) : <div />}
                <div style={{ textAlign: 'right', minWidth: 70 }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', fontFamily: 'monospace' }}>{fmt(tierPrice(s))} {curSym(s.pd_currency_code)}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)', display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 12, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-secondary)', borderRadius: 6,
            fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}>Vazgeç</button>
          <button onClick={applyAll} disabled={selectedCount === 0} style={{
            flex: 2, padding: 12, background: 'var(--success)', color: '#fff',
            border: 'none', borderRadius: 6,
            fontSize: '15px', fontWeight: 800, cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
            opacity: selectedCount === 0 ? 0.5 : 1,
          }}>✓ {selectedCount} Ürünü Sepete Ekle</button>
        </div>
      </div>
    </div>
  );
}

// ── Müşteri seçimi modal'ı ─────────────────────────────────────────
function CustomerPickModal({ onPick, onClose, branchId = null, branchName = null }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({
    title: '', type: 'customer', identity_type: 'individual',
    phone: '', email: '',
    identity_number: '',  // TC
    tax_number: '', tax_office: '',
    address: '', city: '', district: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);

  // Şehir listesi yükle (modal açıldığında)
  useEffect(() => {
    if (showNew && cities.length === 0) {
      api.get('/locations/cities?country=Türkiye').catch(() => null)
        .then(r => { if (r?.data?.success) setCities(r.data.data || []); });
    }
  }, [showNew, cities.length]);

  // Şehir değişince ilçeleri yükle
  useEffect(() => {
    if (!newForm.city) { setDistricts([]); return; }
    api.get('/locations/districts?city=' + encodeURIComponent(newForm.city)).catch(() => null)
      .then(r => { if (r?.data?.success) setDistricts(r.data.data || []); });
  }, [newForm.city]);

  useEffect(() => {
    if (!q.trim() || q.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await api.get('/accounts?search=' + encodeURIComponent(q) + '&limit=20');
        if (r.data.success) {
          const items = r.data.data?.items || r.data.data || [];
          setResults(items);
        }
      } catch {}
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const createCustomer = async () => {
    setErr('');
    if (!newForm.title?.trim()) { setErr('Cari ünvanı zorunludur'); return; }

    // Tüzel kişide vergi numarası zorunlu
    if (newForm.identity_type === 'corporate' && !newForm.tax_number?.trim()) {
      setErr('Tüzel kişi için Vergi Numarası (VKN) zorunludur');
      return;
    }

    // Gerçek kişide TC boşsa default 11111111111 ata
    const tcDefault = '11111111111';
    const identityNumber = newForm.identity_type === 'individual'
      ? (newForm.identity_number?.trim() || tcDefault)
      : null;

    setSaving(true);
    try {
      const body = {
        title: newForm.title.trim(),
        type: newForm.type || 'customer',
        identity_type: newForm.identity_type || 'individual',
        phone: newForm.phone || null,
        email: newForm.email || null,
        identity_number: identityNumber,
        tax_number: newForm.identity_type === 'corporate' ? newForm.tax_number.trim() : null,
        tax_office: newForm.tax_office || null,
        address: newForm.address || null,
        city: newForm.city || null,
        district: newForm.district || null,
        branch_id: branchId || null,    // PoS'un atandığı şube — değiştirilemez
        is_active: true,
      };
      const r = await api.post('/accounts', body);
      if (r.data.success) {
        const newId = r.data.data?.id;
        if (newId) {
          const det = await api.get('/accounts/' + newId);
          if (det.data?.success) {
            onPick(det.data.data);
            return;
          }
        }
        onPick({ id: newId, title: body.title });
      }
    } catch (e) {
      setErr(e.response?.data?.message || 'Müşteri eklenemedi');
    } finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 10, width: 'min(560px, 92vw)',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex' }}>
          <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>{showNew ? '➕ Yeni Müşteri' : '👤 Müşteri Seç'}</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>

        {showNew ? (
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
            {err && <div style={{ padding: 8, background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', borderRadius: 6, fontSize: '13px' }}>{err}</div>}

            {/* Şube — PoS'un atandığı şube, otomatik + readonly */}
            <div>
              <label style={newCustLabel}>Bağlı Şube <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}>(otomatik)</span></label>
              <input
                value={branchName || (branchId ? '— şube atanmış —' : '⚠ Şube tanımlı değil — Ayarlar → Perakende Satış → Varsayılan Şube')}
                readOnly
                title="Bu PoS cihazının atandığı şube — değiştirilemez"
                style={{
                  ...newCustInput, background: 'var(--bg-secondary)', cursor: 'not-allowed',
                  color: branchId ? 'var(--text-primary)' : 'var(--danger)', fontWeight: 600,
                }}
              />
            </div>

            {/* Cari Tip + Kişi Tipi */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={newCustLabel}>Cari Tipi</label>
                <select value={newForm.type} onChange={(e) => setNewForm(p => ({ ...p, type: e.target.value }))}
                  style={newCustInput}>
                  <option value="customer">Müşteri</option>
                  <option value="retail_customer">Perakende Müşteri</option>
                  <option value="supplier">Tedarikçi</option>
                  <option value="both">Müşteri + Tedarikçi</option>
                  <option value="dealer">Bayi</option>
                </select>
              </div>
              <div>
                <label style={newCustLabel}>Kişi Tipi</label>
                <select value={newForm.identity_type} onChange={(e) => setNewForm(p => ({ ...p, identity_type: e.target.value }))}
                  style={newCustInput}>
                  <option value="individual">Şahıs (TC)</option>
                  <option value="corporate">Kurumsal (VKN)</option>
                </select>
              </div>
            </div>

            <div>
              <label style={newCustLabel}>Ad Soyad / Ünvan <span style={{ color: 'var(--danger)' }}>*</span></label>
              <input autoFocus value={newForm.title} onChange={(e) => setNewForm(p => ({ ...p, title: e.target.value }))}
                placeholder={newForm.identity_type === 'corporate' ? 'ABC Ticaret Ltd. Şti.' : 'Ahmet Yılmaz'}
                style={newCustInput} />
            </div>

            {/* TC veya Vergi No (identity tipine göre) + Vergi Dairesi */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {newForm.identity_type === 'individual' ? (
                <div>
                  <label style={newCustLabel}>TC Kimlik No <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(boş ise 1111... atanır)</span></label>
                  <input value={newForm.identity_number} onChange={(e) => setNewForm(p => ({ ...p, identity_number: e.target.value.replace(/\D/g, '').slice(0, 11) }))}
                    placeholder="11 hane veya boş" style={{ ...newCustInput, fontFamily: 'monospace' }} />
                </div>
              ) : (
                <div>
                  <label style={newCustLabel}>Vergi No (VKN) <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input value={newForm.tax_number} onChange={(e) => setNewForm(p => ({ ...p, tax_number: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    placeholder="10 hane (zorunlu)" required
                    style={{ ...newCustInput, fontFamily: 'monospace',
                      borderColor: newForm.tax_number?.trim() ? 'var(--border-secondary)' : 'var(--warning)' }} />
                </div>
              )}
              <div>
                <label style={newCustLabel}>Vergi Dairesi</label>
                <input value={newForm.tax_office} onChange={(e) => setNewForm(p => ({ ...p, tax_office: e.target.value }))}
                  placeholder="Örn. Kadıköy" style={newCustInput} />
              </div>
            </div>

            {/* İletişim */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={newCustLabel}>Telefon</label>
                <input value={newForm.phone} onChange={(e) => setNewForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="0532..." style={newCustInput} />
              </div>
              <div>
                <label style={newCustLabel}>E-posta</label>
                <input type="email" value={newForm.email} onChange={(e) => setNewForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="ornek@mail.com" style={newCustInput} />
              </div>
            </div>

            {/* Şehir + İlçe — seçmeli */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={newCustLabel}>Şehir</label>
                <select value={newForm.city} onChange={(e) => setNewForm(p => ({ ...p, city: e.target.value, district: '' }))}
                  style={newCustInput}>
                  <option value="">— Seçin —</option>
                  {cities.map(c => <option key={c.id || c.name} value={c.name || c}>{c.name || c}</option>)}
                </select>
              </div>
              <div>
                <label style={newCustLabel}>İlçe</label>
                <select value={newForm.district} onChange={(e) => setNewForm(p => ({ ...p, district: e.target.value }))}
                  disabled={!newForm.city}
                  style={{ ...newCustInput, opacity: newForm.city ? 1 : 0.5 }}>
                  <option value="">— Seçin —</option>
                  {districts.map(d => <option key={d.id || d.name} value={d.name || d}>{d.name || d}</option>)}
                </select>
              </div>
            </div>

            {/* Adres */}
            <div>
              <label style={newCustLabel}>Adres</label>
              <textarea value={newForm.address} onChange={(e) => setNewForm(p => ({ ...p, address: e.target.value }))}
                placeholder="Mahalle, sokak, no..." rows={2}
                style={{ ...newCustInput, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 6, position: 'sticky', bottom: 0, background: 'var(--bg-card)', paddingTop: 8 }}>
              <button onClick={() => { setShowNew(false); setErr(''); }} disabled={saving} style={{
                flex: 1, padding: 12, background: 'var(--bg-secondary)',
                border: '1px solid var(--border-secondary)', borderRadius: 6,
                fontSize: '14px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
              }}>← Geri</button>
              <button onClick={createCustomer} disabled={saving} style={{
                flex: 2, padding: 12, background: 'var(--success)', color: '#fff',
                border: 'none', borderRadius: 6,
                fontSize: '14px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}>{saving ? 'Kaydediliyor...' : '✓ Cari Ekle ve Seç'}</button>
            </div>
          </div>
        ) : (
        <>
        <div style={{ padding: 12 }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Ad, kod, telefon, cep, VKN, TC, e-posta..."
            style={{ width: '100%', padding: '12px 14px', fontSize: '15px',
              border: '2px solid var(--accent-primary)', borderRadius: 8, outline: 'none' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
            <button onClick={() => onPick(null)} style={{
              padding: 10, background: 'rgba(99,102,241,0.1)',
              border: '1px solid rgba(99,102,241,0.35)', borderRadius: 6, cursor: 'pointer',
              color: '#6366f1', fontWeight: 700,
            }}>↩ Varsayılan Cariye Dön</button>
            <button onClick={() => { setShowNew(true); setNewForm(p => ({ ...p, title: q.trim() })); }} style={{
              padding: 10, background: 'var(--success)', color: '#fff',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700,
            }}>➕ Yeni Müşteri Ekle</button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
          {results.map(c => (
            <button key={c.id} onClick={() => onPick(c)} style={{
              display: 'block', width: '100%', padding: 10, marginBottom: 6,
              background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)',
              borderRadius: 6, cursor: 'pointer', textAlign: 'left',
            }}>
              <div style={{ fontWeight: 700 }}>{c.title || c.name}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {c.code}{c.phone ? ' · ' + c.phone : ''}{c.balance != null ? ' · Bakiye: ' + fmt(c.balance) : ''}
              </div>
            </button>
          ))}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ── Personel seçim modal'ı ─────────────────────────────────────────
function PersonnelPickModal({ personnel, active, target, onPick, onClose }) {
  const [q, setQ] = useState('');
  const filtered = !q.trim() ? personnel : personnel.filter(p =>
    (p.full_name || '').toLowerCase().includes(q.toLowerCase()) ||
    (p.code || '').toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 10, width: 'min(520px, 92vw)',
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 700 }}>👨‍💼 Personel Seç</div>
            {target && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: 2 }}>Hedef: {target}</div>}
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 12 }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Ad veya kod yazın..."
            style={{ width: '100%', padding: '12px 14px', fontSize: '15px',
              border: '2px solid var(--accent-primary)', borderRadius: 8, outline: 'none' }} />
          <button onClick={() => onPick(null)} style={{
            marginTop: 10, width: '100%', padding: 10, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-secondary)', borderRadius: 6, cursor: 'pointer',
          }}>✕ Personel atama</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
          {filtered.map(p => (
            <button key={p.id} onClick={() => onPick(p)} style={{
              display: 'block', width: '100%', padding: 10, marginBottom: 6,
              background: active?.id === p.id ? 'rgba(16,185,129,0.15)' : 'var(--bg-secondary)',
              border: '1px solid ' + (active?.id === p.id ? 'var(--success)' : 'var(--border-secondary)'),
              borderRadius: 6, cursor: 'pointer', textAlign: 'left',
            }}>
              <div style={{ fontWeight: 700 }}>{p.full_name}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                {p.code}{p.department ? ' · ' + p.department : ''}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── İskonto modal'ı (1-3 seviye, % veya Tutar) ─────────────────────
function DiscountModal({ levels, value, totals, onApply, onClose }) {
  const [v, setV] = useState(value);
  const upd = (k, patch) => setV(prev => ({ ...prev, [k]: { ...prev[k], ...patch } }));
  const calcAmount = (d) => {
    if (!d.value) return 0;
    if (d.type === 'percent') return totals.total * (parseFloat(d.value) || 0) / 100;
    return parseFloat(d.value) || 0;
  };
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 10, width: 'min(460px, 95vw)',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex' }}>
          <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>🏷️ İskonto Uygula</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3].slice(0, levels).map(n => {
            const key = 'd' + n;
            const d = v[key];
            const amt = calcAmount(d);
            return (
              <div key={key} style={{
                padding: 10, border: '1px solid var(--border-secondary)', borderRadius: 6,
                background: 'var(--bg-secondary)',
              }}>
                <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: 6, color: 'var(--text-secondary)' }}>İskonto {n}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => upd(key, { type: 'percent' })} style={{
                    flex: 1, padding: '8px', background: d.type === 'percent' ? 'var(--accent-primary)' : 'var(--bg-card)',
                    color: d.type === 'percent' ? '#fff' : 'var(--text-primary)',
                    border: '1px solid var(--border-secondary)', borderRadius: 6,
                    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                  }}>%  Yüzde</button>
                  <button onClick={() => upd(key, { type: 'amount' })} style={{
                    flex: 1, padding: '8px', background: d.type === 'amount' ? 'var(--accent-primary)' : 'var(--bg-card)',
                    color: d.type === 'amount' ? '#fff' : 'var(--text-primary)',
                    border: '1px solid var(--border-secondary)', borderRadius: 6,
                    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                  }}>Tutar</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <input type="number" step="any" value={d.value || ''}
                    onChange={(e) => upd(key, { value: e.target.value })}
                    placeholder="0"
                    style={{ flex: 1, padding: '10px 12px', fontSize: '16px', fontFamily: 'monospace',
                      border: '2px solid var(--accent-primary)', borderRadius: 6, outline: 'none', textAlign: 'right', fontWeight: 700 }} />
                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-tertiary)', minWidth: 40 }}>
                    {d.type === 'percent' ? '%' : '₺'}
                  </span>
                </div>
                {amt > 0 && (
                  <div style={{ marginTop: 6, fontSize: '11px', color: 'var(--success)', textAlign: 'right' }}>
                    ≈ -{fmt(amt)} ₺ indirim
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)', display: 'flex', gap: 8 }}>
          <button onClick={() => onApply({ ...v, d1: { type: 'percent', value: 0 }, d2: { type: 'percent', value: 0 }, d3: { type: 'percent', value: 0 } })}
            style={{ padding: '10px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
            Sıfırla
          </button>
          <button onClick={onClose} style={{
            flex: 1, padding: 12, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-secondary)', borderRadius: 6,
            fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}>Vazgeç</button>
          <button onClick={() => onApply(v)} style={{
            flex: 2, padding: 12, background: 'var(--success)', color: '#fff',
            border: 'none', borderRadius: 6,
            fontSize: '15px', fontWeight: 800, cursor: 'pointer',
          }}>✓ Uygula</button>
        </div>
      </div>
    </div>
  );
}

// ── Yuvarlama modal'ı ─────────────────────────────────────────────
function RoundingModal({ value, totals, onApply, onClose }) {
  const [v, setV] = useState(value);
  // focused: 'amount' veya 'percent' — keypad hangi kutucuğa yazıyor
  const [focused, setFocused] = useState(value?.type === 'percent' ? 'percent' : 'amount');
  const targetTotal = totals.total - (totals.disc1 || 0) - (totals.disc2 || 0) - (totals.disc3 || 0);

  // İki kutucuğun anlık değeri — birinin diğerinden hesaplanması
  const amountVal = v.type === 'amount'
    ? (v.value || '')
    : (v.value ? ((parseFloat(v.value) || 0) * targetTotal / 100).toFixed(2) : '');
  const percentVal = v.type === 'percent'
    ? (v.value || '')
    : (v.value ? (targetTotal > 0 ? ((parseFloat(v.value) || 0) * 100 / targetTotal).toFixed(4) : '0') : '');

  const setAmount = (val) => setV({ type: 'amount', value: val });
  const setPercent = (val) => setV({ type: 'percent', value: val });

  // Otomatik yuvarlama hesapla (en yakın 0.50, 1, 5, 10'a)
  const autoRound = (target) => {
    const diff = targetTotal - Math.round(targetTotal / target) * target;
    setV({ type: 'amount', value: diff.toFixed(2) });
    setFocused('amount');
  };

  const press = (k) => {
    const setVal = focused === 'percent' ? setPercent : setAmount;
    const cur = focused === 'percent' ? String(percentVal || '') : String(amountVal || '');
    if (k === 'C') return setVal('');
    if (k === '←') return setVal(cur.slice(0, -1));
    if (k === '+/-') return setVal(cur.startsWith('-') ? cur.slice(1) : ('-' + cur));
    if (k === '.') {
      if (!cur.includes('.')) setVal(cur + '.');
      return;
    }
    if (k === '00') return setVal(cur + '00');
    setVal(cur + k);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 10, width: 'min(420px, 95vw)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex' }}>
          <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>⊕ Yuvarlama</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Mevcut tutar: <strong>{fmt(targetTotal)} ₺</strong>
          </div>

          {/* İki kutucuk yan yana — biri yazınca diğeri hesaplar */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {/* TUTAR */}
            <div onClick={() => setFocused('amount')} style={{
              padding: 10, borderRadius: 6,
              border: '2px solid ' + (focused === 'amount' ? 'var(--accent-primary)' : 'var(--border-secondary)'),
              cursor: 'pointer', background: focused === 'amount' ? 'rgba(99,102,241,0.06)' : 'var(--bg-secondary)',
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, fontWeight: 700 }}>
                Tutar (₺)
              </div>
              <input type="text" value={amountVal}
                onFocus={() => setFocused('amount')}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.\-]/g, ''))}
                placeholder="0.00"
                style={{ width: '100%', background: 'transparent', border: 'none',
                  fontSize: '22px', fontFamily: 'monospace', fontWeight: 800,
                  textAlign: 'right', outline: 'none', color: 'var(--text-primary)' }} />
            </div>
            {/* YÜZDE */}
            <div onClick={() => setFocused('percent')} style={{
              padding: 10, borderRadius: 6,
              border: '2px solid ' + (focused === 'percent' ? 'var(--accent-primary)' : 'var(--border-secondary)'),
              cursor: 'pointer', background: focused === 'percent' ? 'rgba(99,102,241,0.06)' : 'var(--bg-secondary)',
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, fontWeight: 700 }}>
                Yüzde (%)
              </div>
              <input type="text" value={percentVal}
                onFocus={() => setFocused('percent')}
                onChange={(e) => setPercent(e.target.value.replace(/[^0-9.\-]/g, ''))}
                placeholder="0"
                style={{ width: '100%', background: 'transparent', border: 'none',
                  fontSize: '22px', fontFamily: 'monospace', fontWeight: 800,
                  textAlign: 'right', outline: 'none', color: 'var(--text-primary)' }} />
            </div>
          </div>
          {/* Numerik tuş takımı — ana ekranla aynı görünüm */}
          <Keypad
            compact
            onPress={press}
            keys={[
              { k: '1' }, { k: '2' }, { k: '3' },
              { k: 'C', label: '⌫\nSil', color: '#ef4444', textColor: '#fff', action: () => press('←') },
              { k: '4' }, { k: '5' }, { k: '6' },
              { k: 'pm', label: '±\nİşaret', color: '#0891b2', textColor: '#fff', action: () => press('+/-') },
              { k: '7' }, { k: '8' }, { k: '9' },
              { k: 'clr', label: '✕\nTemizle', color: '#dc2626', textColor: '#fff', action: () => press('C') },
              { k: '0' }, { k: '00' }, { k: '.' },
              { k: 'apply', label: '✓\nUygula', color: 'var(--success)', textColor: '#fff', action: () => onApply(v) },
            ]}
          />
        </div>
        <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)', display: 'flex', gap: 8 }}>
          <button onClick={() => onApply({ type: 'amount', value: 0 })} style={{
            padding: '10px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-secondary)', borderRadius: 6, fontWeight: 600, cursor: 'pointer',
          }}>Sıfırla</button>
          <button onClick={onClose} style={{
            flex: 1, padding: 12, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-secondary)', borderRadius: 6,
            fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}>Vazgeç</button>
          <button onClick={() => onApply(v)} style={{
            flex: 2, padding: 12, background: 'var(--success)', color: '#fff',
            border: 'none', borderRadius: 6,
            fontSize: '15px', fontWeight: 800, cursor: 'pointer',
          }}>✓ Uygula</button>
        </div>
      </div>
    </div>
  );
}

// ── Satır İskonto modalı — seçili satıra d1/d2/d3 uygular ────────
function LineDiscountModal({ line, levels, onApply, onClose }) {
  const qty = parseFloat(line.quantity) || 0;
  const price = parseFloat(line.unit_price) || 0;
  const vatRate = parseFloat(line.vat_rate) || 0;
  const lineGross = qty * price * (1 + vatRate / 100);

  // Her seviye için { type: 'percent'|'amount', value: '' }
  // Başlangıçta line.discount_rate1/2/3 % olarak gelir
  const initState = (rate) => ({ type: 'percent', value: rate > 0 ? String(rate) : '' });
  const [d1, setD1] = useState(initState(parseFloat(line.discount_rate1) || 0));
  const [d2, setD2] = useState(initState(parseFloat(line.discount_rate2) || 0));
  const [d3, setD3] = useState(initState(parseFloat(line.discount_rate3) || 0));
  // Hangi satırın hangi kutucuğu odakta: {level: 1|2|3, field: 'amount'|'percent'}
  const [focused, setFocused] = useState({ level: 1, field: 'percent' });

  // Önceki iskontoların sonrasındaki taban (zincirleme)
  const baseFor = (level) => {
    let base = lineGross;
    const apply = (d) => {
      const pct = d.type === 'percent'
        ? (parseFloat(d.value) || 0)
        : (base > 0 ? ((parseFloat(d.value) || 0) * 100 / base) : 0);
      base = base * (1 - pct / 100);
    };
    if (level >= 2) apply(d1);
    if (level >= 3) apply(d2);
    return base;
  };

  // Bir seviyenin gösterilen amount ve percent değerlerini hesapla
  const computed = (d, base) => {
    const amount = d.type === 'amount'
      ? (d.value || '')
      : (d.value ? ((parseFloat(d.value) || 0) * base / 100).toFixed(2) : '');
    const percent = d.type === 'percent'
      ? (d.value || '')
      : (d.value ? (base > 0 ? ((parseFloat(d.value) || 0) * 100 / base).toFixed(4) : '0') : '');
    return { amount, percent };
  };

  const setter = (lvl) => lvl === 1 ? setD1 : lvl === 2 ? setD2 : setD3;
  const getter = (lvl) => lvl === 1 ? d1 : lvl === 2 ? d2 : d3;

  const press = (k) => {
    const lvl = focused.level;
    const d = getter(lvl);
    const set = setter(lvl);
    const base = baseFor(lvl);
    const view = computed(d, base);
    const cur = focused.field === 'percent' ? String(view.percent || '') : String(view.amount || '');
    const setVal = (v) => set({ type: focused.field, value: v });
    if (k === 'C') return setVal('');
    if (k === '←') return setVal(cur.slice(0, -1));
    if (k === '.') { if (!cur.includes('.')) setVal(cur + '.'); return; }
    if (k === '00') return setVal(cur + '00');
    setVal(cur + k);
  };

  const applyAll = () => {
    // % olarak normalize edip line.discount_rate1/2/3 ye yaz
    const norm = (d, base) => {
      if (!d.value) return 0;
      if (d.type === 'percent') return parseFloat(d.value) || 0;
      return base > 0 ? ((parseFloat(d.value) || 0) * 100 / base) : 0;
    };
    const r1 = levels >= 1 ? norm(d1, lineGross) : 0;
    const base2 = lineGross * (1 - r1 / 100);
    const r2 = levels >= 2 ? norm(d2, base2) : 0;
    const base3 = base2 * (1 - r2 / 100);
    const r3 = levels >= 3 ? norm(d3, base3) : 0;
    onApply({ discount_rate1: r1, discount_rate2: r2, discount_rate3: r3 });
  };

  const resetAll = () => onApply({ discount_rate1: 0, discount_rate2: 0, discount_rate3: 0 });

  const renderLevel = (lvl) => {
    const d = getter(lvl);
    const base = baseFor(lvl);
    const view = computed(d, base);
    const isFocAmt = focused.level === lvl && focused.field === 'amount';
    const isFocPct = focused.level === lvl && focused.field === 'percent';
    return (
      <div key={lvl} style={{ background: 'var(--bg-secondary)', padding: 10, borderRadius: 6, border: '1px solid var(--border-secondary)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <strong style={{ fontSize: '12px' }}>🏷️ İskonto {lvl}</strong>
          <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>
            Taban: {fmt(base)}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <div onClick={() => setFocused({ level: lvl, field: 'amount' })} style={{
            padding: 8, borderRadius: 6,
            border: '2px solid ' + (isFocAmt ? 'var(--accent-primary)' : 'var(--border-secondary)'),
            cursor: 'pointer', background: isFocAmt ? 'rgba(99,102,241,0.06)' : 'var(--bg-card)',
          }}>
            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2, fontWeight: 700 }}>Tutar</div>
            <input type="text" value={view.amount}
              onFocus={() => setFocused({ level: lvl, field: 'amount' })}
              onChange={(e) => setter(lvl)({ type: 'amount', value: e.target.value.replace(/[^0-9.]/g, '') })}
              placeholder="0.00"
              style={{ width: '100%', background: 'transparent', border: 'none',
                fontSize: '17px', fontFamily: 'monospace', fontWeight: 800,
                textAlign: 'right', outline: 'none', color: 'var(--text-primary)' }} />
          </div>
          <div onClick={() => setFocused({ level: lvl, field: 'percent' })} style={{
            padding: 8, borderRadius: 6,
            border: '2px solid ' + (isFocPct ? 'var(--accent-primary)' : 'var(--border-secondary)'),
            cursor: 'pointer', background: isFocPct ? 'rgba(99,102,241,0.06)' : 'var(--bg-card)',
          }}>
            <div style={{ fontSize: '10px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2, fontWeight: 700 }}>Yüzde %</div>
            <input type="text" value={view.percent}
              onFocus={() => setFocused({ level: lvl, field: 'percent' })}
              onChange={(e) => setter(lvl)({ type: 'percent', value: e.target.value.replace(/[^0-9.]/g, '') })}
              placeholder="0"
              style={{ width: '100%', background: 'transparent', border: 'none',
                fontSize: '17px', fontFamily: 'monospace', fontWeight: 800,
                textAlign: 'right', outline: 'none', color: 'var(--text-primary)' }} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 10, width: 'min(460px, 95vw)',
        maxHeight: '95vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>🏷️ Satır İskonto</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
            <span><strong>{line.stock_name}</strong> · {fmt(qty)} {line.unit_code || 'ad'}</span>
            <span>Brüt: <strong>{fmt(lineGross)}</strong></span>
          </div>
          {levels >= 1 && renderLevel(1)}
          {levels >= 2 && renderLevel(2)}
          {levels >= 3 && renderLevel(3)}

          <Keypad
            compact
            onPress={press}
            keys={[
              { k: '1' }, { k: '2' }, { k: '3' },
              { k: 'C', label: '⌫\nSil', color: '#ef4444', textColor: '#fff', action: () => press('←') },
              { k: '4' }, { k: '5' }, { k: '6' },
              { k: 'clr', label: '✕\nTemizle', color: '#dc2626', textColor: '#fff', action: () => press('C') },
              { k: '7' }, { k: '8' }, { k: '9' },
              { k: 'rst', label: '↺\nSıfırla', color: '#64748b', textColor: '#fff', action: resetAll },
              { k: '0' }, { k: '00' }, { k: '.' },
              { k: 'apply', label: '✓\nUygula', color: 'var(--success)', textColor: '#fff', action: applyAll },
            ]}
          />
        </div>
        <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)', display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 12, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-secondary)', borderRadius: 6,
            fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}>Vazgeç</button>
          <button onClick={applyAll} style={{
            flex: 2, padding: 12, background: 'var(--success)', color: '#fff',
            border: 'none', borderRadius: 6,
            fontSize: '15px', fontWeight: 800, cursor: 'pointer',
          }}>✓ Uygula</button>
        </div>
      </div>
    </div>
  );
}

// ── Stok Durum modal'ı (bağımsız — arama + barkod + sonuç) ────────
function StockStatusModal({ onClose, colorSizeEnabled = false }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [data, setData] = useState(null);  // { stock, warehouses, prices }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Arama
  useEffect(() => {
    if (!q.trim() || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/stocks?search=' + encodeURIComponent(q) + '&limit=20');
        if (r.data?.success) setResults(r.data.data?.items || r.data.data || []);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const loadStock = async (stockId, stockName) => {
    setLoading(true); setErr('');
    setResults([]);
    try {
      const [detail, prices, winv] = await Promise.all([
        api.get('/stocks/' + stockId),
        api.get('/stocks/' + stockId + '/prices').catch(() => ({ data: { data: [] } })),
        api.get('/stocks/' + stockId + '/inventory').catch(() => ({ data: { data: [] } })),
      ]);
      setData({
        stock: detail.data?.data || { name: stockName },
        warehouses: winv.data?.data || [],
        prices: prices.data?.data || [],
      });
    } catch (e) {
      setErr(e.response?.data?.message || 'Yüklenemedi');
    } finally { setLoading(false); }
  };

  // Enter ile barkod arama
  const submitBarcode = async () => {
    if (!q.trim()) return;
    try {
      const r = await api.get('/sales-invoices/stock-by-barcode/' + encodeURIComponent(q.trim()));
      if (r.data?.success) {
        loadStock(r.data.data.id, r.data.data.name);
        setQ('');
      } else {
        setErr('Barkod bulunamadı');
      }
    } catch (e) {
      setErr('Barkod bulunamadı: ' + q);
    }
  };

  const { stock, warehouses = [], prices = [] } = data || {};
  const totalQty = warehouses.reduce((acc, w) => acc + (parseFloat(w.quantity) || 0), 0);
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 12, width: 'min(640px, 95vw)',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: 14, borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.5rem' }}>📦</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>Stok Durum Sorgu</div>
            {stock && (
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <strong>{stock.name}</strong>
                {stock.code && <span style={{ fontFamily: 'monospace' }}>{stock.code}</span>}
                {stock.category_name && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 700 }}>📁 {stock.category_name}</span>
                )}
                {colorSizeEnabled && stock.color_name && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(99,102,241,0.15)', color: '#6366f1', fontWeight: 700 }}>🎨 {stock.color_name}</span>
                )}
                {colorSizeEnabled && stock.size_name && (
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 700 }}>📏 {stock.size_name}</span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '22px', cursor: 'pointer' }}>×</button>
        </div>

        {/* Arama / Barkod alanı */}
        <div style={{ padding: 12, borderBottom: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setErr(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') submitBarcode(); }}
              placeholder="Barkod okutun veya ürün adı/kodu yazın..."
              style={{ flex: 1, padding: '12px 14px', fontSize: '15px',
                border: '2px solid var(--accent-primary)', borderRadius: 8, outline: 'none', fontFamily: 'monospace' }} />
            <button onClick={submitBarcode} style={{
              padding: '10px 18px', background: 'var(--accent-primary)', color: '#fff',
              border: 'none', borderRadius: 6, fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            }}>⏎ Sorgula</button>
          </div>
          {err && <div style={{ marginTop: 8, padding: 8, background: 'rgba(239,68,68,0.1)', color: 'var(--danger)', borderRadius: 6, fontSize: '12px' }}>{err}</div>}
          {/* Hızlı arama sonuçları (dropdown) */}
          {results.length > 0 && (
            <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border-secondary)', borderRadius: 6 }}>
              {results.map(s => (
                <button key={s.id} onClick={() => { loadStock(s.id, s.name); setQ(''); }}
                  style={{
                    display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left',
                    background: 'transparent', border: 'none',
                    borderBottom: '1px solid var(--border-secondary)',
                    cursor: 'pointer', fontSize: '13px',
                  }}>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                    {s.code}{s.barcode ? ' · ' + s.barcode : ''}
                  </div>
                  {(s.category_name || (colorSizeEnabled && (s.color_name || s.size_name))) && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                      {s.category_name && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 700 }}>📁 {s.category_name}</span>
                      )}
                      {colorSizeEnabled && s.color_name && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(99,102,241,0.15)', color: '#6366f1', fontWeight: 700 }}>🎨 {s.color_name}</span>
                      )}
                      {colorSizeEnabled && s.size_name && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 'var(--r-full)', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 700 }}>📏 {s.size_name}</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: 14, overflow: 'auto', flex: 1 }}>
          {loading && <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-tertiary)' }}>Yükleniyor...</div>}
          {!loading && !data && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔍</div>
              <div>Bir barkod okutun veya ürün adı yazın</div>
            </div>
          )}
          {data && (<>
          {/* Toplam stok özeti */}
          <div style={{
            padding: '12px 16px', borderRadius: 8, marginBottom: 14,
            background: totalQty > 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: '1px solid ' + (totalQty > 0 ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'),
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1 }}>Toplam Stok</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, fontFamily: 'JetBrains Mono, monospace', color: totalQty > 0 ? 'var(--success)' : 'var(--danger)' }}>
              {fmt(totalQty)} <span style={{ fontSize: '1rem' }}>{stock?.unit_code || ''}</span>
            </div>
            {stock?.min_stock > 0 && totalQty < stock.min_stock && (
              <div style={{ fontSize: '12px', color: 'var(--danger)', fontWeight: 700 }}>
                ⚠ Min stok seviyesinin altında ({stock.min_stock} {stock?.unit_code || ''})
              </div>
            )}
          </div>

          {/* Depo bazlı dağılım */}
          <div style={{ marginBottom: 14 }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>🏬 Depo Bazlı</h3>
            {warehouses.length === 0 ? (
              <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                Hiçbir depoda stok yok
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {warehouses.filter(w => parseFloat(w.quantity) !== 0).map(w => {
                  const qty = parseFloat(w.quantity) || 0;
                  return (
                    <div key={w.warehouse_id || w.id} style={{
                      display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10,
                      padding: '10px 12px', background: 'var(--bg-secondary)',
                      borderRadius: 6, alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{w.warehouse_name || w.name || w.warehouse_code}</div>
                        {w.warehouse_code && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>{w.warehouse_code}</div>}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '15px', color: qty > 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {fmt(qty)} {stock?.unit_code || ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Fiyat listeleri — sadece perakende + eticaret */}
          {(() => {
            const filtered = (prices || []).filter(p => ['perakende', 'eticaret'].includes((p.price_group || '').toLowerCase()));
            return (
              <div>
                <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>💰 Fiyat Listeleri (Perakende & E-Ticaret)</h3>
                {filtered.length === 0 ? (
                  <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                    Perakende veya e-ticaret fiyat tanımı yok
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-secondary)' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)' }}>GRUP</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)' }}>LİSTE</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)' }}>FİYAT 1</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)' }}>FİYAT 2</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)' }}>FİYAT 3</th>
                        <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, fontSize: '11px', color: 'var(--text-secondary)' }}>KDV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(p => {
                        const cur = p.item_currency_code || p.def_currency_code || 'TRY';
                        const sym = curSym(cur);
                        return (
                          <tr key={p.price_definition_id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 'var(--r-full)', background: p.price_group === 'perakende' ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.12)', color: p.price_group === 'perakende' ? '#f59e0b' : '#6366f1', fontSize: '11px', fontWeight: 700 }}>
                                {p.price_group}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', fontWeight: 600 }}>{p.name}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                              {p.price1 != null ? <>{fmt(p.price1)} <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{sym}</span></> : '—'}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                              {p.price2 != null ? <>{fmt(p.price2)} <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{sym}</span></> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                              {p.price3 != null ? <>{fmt(p.price3)} <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{sym}</span></> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                            </td>
                            <td style={{ padding: '8px 10px', textAlign: 'center', fontSize: '12px' }}>
                              {p.vat_included ? '✓ Dahil' : '— Hariç'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })()}
          </>)}
        </div>

        <div style={{ padding: 12, borderTop: '1px solid var(--border-secondary)', textAlign: 'right' }}>
          <button onClick={onClose} style={{
            padding: '10px 24px', background: 'var(--accent-primary)', color: '#fff',
            border: 'none', borderRadius: 6, fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          }}>Kapat</button>
        </div>
      </div>
    </div>
  );
}

// ── Tartılı ürün miktar modal'ı ────────────────────────────────────
function WeightedQtyModal({ stock, priceTier = 1, onConfirm, onClose }) {
  const tierP = (() => {
    const v = stock['pd_price' + priceTier];
    if (v != null && parseFloat(v) > 0) return parseFloat(v);
    return parseFloat(stock.pd_price1) || 0;
  })();
  const [qty, setQty] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const press = (k) => {
    if (k === 'C') return setQty('');
    if (k === '←') return setQty(prev => prev.slice(0, -1));
    if (k === '.') {
      if (!qty.includes('.')) setQty(prev => prev + '.');
      return;
    }
    if (k === '00') return setQty(prev => prev + '00');
    setQty(prev => prev + k);
  };
  const confirm = () => {
    const val = parseFloat((qty || '').replace(',', '.'));
    onConfirm(val > 0 ? val : 1);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: 12, width: 'min(380px, 95vw)',
        padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: '1.5rem' }}>⚖</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{stock.name}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
              {stock.code} · {fmt(tierP)} {curSym(stock.pd_currency_code)} / {stock.unit_code || 'kg'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '22px', cursor: 'pointer' }}>×</button>
        </div>

        {/* Miktar input */}
        <div style={{
          padding: '14px 18px', textAlign: 'right',
          background: 'var(--accent-primary)', color: '#fff', borderRadius: 8,
        }}>
          <div style={{ fontSize: '11px', opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>Miktar ({stock.unit_code || 'kg'})</div>
          <input
            ref={inputRef}
            type="text"
            value={qty}
            onChange={(e) => setQty(e.target.value.replace(/[^0-9.,]/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
            placeholder="1"
            style={{
              width: '100%', background: 'transparent', border: 'none',
              fontSize: '2.4rem', fontFamily: 'JetBrains Mono, monospace',
              fontWeight: 800, color: '#fff', textAlign: 'right', outline: 'none',
            }}
          />
          {qty && (
            <div style={{ fontSize: '11px', opacity: 0.9, marginTop: 2 }}>
              ≈ Toplam: {fmt((parseFloat(qty.replace(',', '.')) || 0) * tierP)} {curSym(stock.pd_currency_code)}
            </div>
          )}
        </div>

        {/* Numerik tuş takımı — ana ekranla aynı */}
        <Keypad
          compact
          onPress={press}
          keys={[
            { k: '1' }, { k: '2' }, { k: '3' },
            { k: 'sil', label: '⌫\nSil', color: '#ef4444', textColor: '#fff', action: () => press('←') },
            { k: '4' }, { k: '5' }, { k: '6' },
            { k: 'half', label: '½\n0.5', color: '#f59e0b', textColor: '#fff', action: () => setQty('0.5') },
            { k: '7' }, { k: '8' }, { k: '9' },
            { k: 'one', label: '1\nBirim', color: '#0891b2', textColor: '#fff', action: () => setQty('1') },
            { k: '0' }, { k: '00' }, { k: '.' },
            { k: 'ok', label: '✓\nEkle', color: 'var(--success)', textColor: '#fff', action: () => onConfirm(parseFloat((qty || '').replace(',', '.')) || 1) },
          ]}
        />

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 14, background: 'var(--bg-secondary)',
            border: '1px solid var(--border-secondary)', borderRadius: 8,
            fontSize: '14px', fontWeight: 700, cursor: 'pointer',
          }}>Vazgeç</button>
          <button onClick={confirm} style={{
            flex: 2, padding: 14, background: 'var(--success)', color: '#fff',
            border: 'none', borderRadius: 8,
            fontSize: '15px', fontWeight: 800, cursor: 'pointer',
          }}>✓ Sepete Ekle{!qty && ' (1 birim)'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Ödeme modal'ı (SPLIT PAYMENT, DİNAMİK) ─────────────────────────
// Nakit (tekil) + N adet Kredi Kartı (her biri banka seçimli — farklı POS)
// + N adet Banka Havale (her biri banka seçimli) + Alış-Veriş Çeki (tekil)
// Kalan otomatik Cari'ye yazılır.
function PaymentModal({ total, customer, onComplete, onClose, saving, hasCashRegister, hasBankAccount, bankAccounts = [], posDefinitions = [], availableVouchers = [], permissions = {} }) {
  const allowCard     = permissions.allowCreditCard   !== false;
  const allowTransfer = permissions.allowBankTransfer !== false;
  const allowVoucher  = permissions.allowVoucher      !== false;
  const allowOnAcct   = permissions.allowOnAccount    !== false;
  const defaultBankId = bankAccounts[0]?.id || '';
  const defaultPosId = posDefinitions[0]?.id || '';
  const INSTALLMENT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const uid = () => Math.random().toString(36).slice(2, 9);
  const [cash, setCash] = useState(total.toFixed(2));
  const [cards, setCards] = useState([]);          // [{ id, amount, pos_definition_id, installment_count, card_type }]
  const [transfers, setTransfers] = useState([]);  // [{ id, amount, bank_account_id }]
  const [vouchers, setVouchers] = useState([]);    // [{ id, voucher_id, amount }] — müşterinin issued çekleri
  const [activeRow, setActiveRow] = useState('cash');
  const [cashManual, setCashManual] = useState(false); // kullanıcı nakit alanına dokunduysa true

  // Bir voucher zaten seçilmişse listede tekrar gösterme
  const selectedVoucherIds = new Set(vouchers.map(v => v.voucher_id).filter(Boolean));

  const cardSum     = cards.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const transferSum = transfers.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const voucherSum  = vouchers.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const nonCashSum  = cardSum + transferSum + voucherSum;

  // Kart/havale/voucher değiştikçe nakit otomatik resync — kullanıcı manuel girene kadar.
  // Manuel girdiyse (cashManual=true) artık otomatik düşülmez (kullanıcı para üstü için fazla nakit girmiş olabilir).
  React.useEffect(() => {
    if (!cashManual) {
      setCash(Math.max(0, total - nonCashSum).toFixed(2));
    }
  }, [nonCashSum, total, cashManual]);

  const cashGiven   = parseFloat(cash) || 0;
  // Para üstü SADECE nakitten verilir. Diğer yöntemlerin fazlası sayılmaz (kart 1100 yazıp 100 para üstü mantıksız).
  // Nakit ne kadar gerekiyor? total - (kart+havale+voucher), eksiyse 0.
  const cashNeeded  = Math.max(0, total - nonCashSum);
  const overpaid    = Math.max(0, cashGiven - cashNeeded); // para üstü — sadece nakitten
  const appliedCash = Math.min(cashGiven, cashNeeded);     // satışa uygulanan nakit
  const sum         = appliedCash + nonCashSum;            // satışa uygulanan toplam
  const onAccount   = Math.max(0, total - sum);            // cariye kalan
  const insufficient = sum < total - 0.01 && !customer;     // müşteri yoksa cariye yazamaz

  const getActiveValue = () => {
    if (activeRow === 'cash') return cash;
    if (activeRow.startsWith('card_'))     return cards.find(c => c.id === activeRow.slice(5))?.amount ?? '';
    if (activeRow.startsWith('transfer_')) return transfers.find(c => c.id === activeRow.slice(9))?.amount ?? '';
    if (activeRow.startsWith('voucher_'))  return vouchers.find(c => c.id === activeRow.slice(8))?.amount ?? '';
    return '';
  };
  const setActiveValue = (v) => {
    if (activeRow === 'cash') { setCashManual(true); return setCash(v); }
    if (activeRow.startsWith('card_')) {
      const id = activeRow.slice(5);
      return setCards(prev => prev.map(c => c.id === id ? { ...c, amount: v } : c));
    }
    if (activeRow.startsWith('transfer_')) {
      const id = activeRow.slice(9);
      return setTransfers(prev => prev.map(c => c.id === id ? { ...c, amount: v } : c));
    }
    if (activeRow.startsWith('voucher_')) {
      const id = activeRow.slice(8);
      return setVouchers(prev => prev.map(c => c.id === id ? { ...c, amount: v } : c));
    }
  };
  const numPress = (k) => {
    const cur = getActiveValue() || '';
    if (k === 'C') return setActiveValue('');
    if (k === '←') return setActiveValue(cur.slice(0, -1));
    if (k === '.') { if (!cur.includes('.')) setActiveValue(cur + '.'); return; }
    if (k === '00') return setActiveValue(cur + '00');
    setActiveValue(cur + k);
  };

  const addCard = () => {
    const remaining = Math.max(0, total - sum).toFixed(2);
    const id = uid();
    setCards(prev => [...prev, { id, amount: remaining, pos_definition_id: defaultPosId, installment_count: 1, card_type: 'credit' }]);
    setActiveRow('card_' + id);
  };
  const addTransfer = () => {
    const remaining = Math.max(0, total - sum).toFixed(2);
    const id = uid();
    setTransfers(prev => [...prev, { id, amount: remaining, bank_account_id: defaultBankId }]);
    setActiveRow('transfer_' + id);
  };
  const addVoucher = () => {
    // ilk seçilmemiş voucher'ı default getir
    const firstFree = availableVouchers.find(v => !selectedVoucherIds.has(v.id));
    const id = uid();
    setVouchers(prev => [...prev, { id, voucher_id: firstFree?.id || '', amount: firstFree?.amount?.toString() || '0' }]);
    setActiveRow('voucher_' + id);
  };
  const removeCard = (id) => {
    setCards(prev => prev.filter(c => c.id !== id));
    if (activeRow === 'card_' + id) setActiveRow('cash');
  };
  const removeTransfer = (id) => {
    setTransfers(prev => prev.filter(c => c.id !== id));
    if (activeRow === 'transfer_' + id) setActiveRow('cash');
  };
  const removeVoucher = (id) => {
    setVouchers(prev => prev.filter(c => c.id !== id));
    if (activeRow === 'voucher_' + id) setActiveRow('cash');
  };
  const setCardField = (id, field, value) => setCards(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  const setTransferField = (id, field, value) => setTransfers(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  const setVoucherField = (id, field, value) => setVouchers(prev => prev.map(c => {
    if (c.id !== id) return c;
    // voucher_id seçildiğinde amount o çekin tutarına otomatik ayarla
    if (field === 'voucher_id') {
      const v = availableVouchers.find(x => x.id === value);
      return { ...c, voucher_id: value, amount: v?.amount?.toString() || c.amount };
    }
    return { ...c, [field]: value };
  }));

  const handleComplete = () => {
    if (saving) return;
    const missCard = cards.find(c => (parseFloat(c.amount) || 0) > 0.01 && !c.pos_definition_id);
    if (missCard) { alert('Kredi kartı satırlarından birinde POS seçilmedi'); return; }
    const missBank = transfers.find(c => (parseFloat(c.amount) || 0) > 0.01 && !c.bank_account_id);
    if (missBank) { alert('Havale satırlarından birinde banka seçilmedi'); return; }
    const missVoucher = vouchers.find(c => (parseFloat(c.amount) || 0) > 0.01 && !c.voucher_id);
    if (missVoucher) { alert('Alış-Veriş Çeki satırlarından birinde çek seçilmedi'); return; }
    // Para üstü SADECE nakitten verilir. Kart/havale/voucher toplamı satışı geçmemeli.
    if (nonCashSum > total + 0.01) {
      alert(`Kart + havale + çek toplamı (${nonCashSum.toFixed(2)} ₺) fatura tutarını (${total.toFixed(2)} ₺) aşamaz.\nFazla tutarı düşürün; para üstü sadece nakitten verilir.`);
      return;
    }
    if (insufficient) { alert('Eksik tutar var ve cariye yazmak için müşteri seçilmeli'); return; }
    if (onAccount > 0.01 && !allowOnAcct) {
      alert(`${onAccount.toFixed(2)} ₺ cariye kalıyor ama cari açık hesap yetkiniz yok — tam ödeme alın.`); return;
    }
    if (onAccount > 0.01 && !customer) {
      alert(`${onAccount.toFixed(2)} ₺ cariye kalıyor — önce müşteri seçin`); return;
    }
    onComplete({
      cash: appliedCash, // sadece satışa uygulanan kısım (para üstü kasaya girmez, kasiyer iade eder)
      cards: cards
        .filter(c => (parseFloat(c.amount) || 0) > 0.01)
        .map(c => ({
          amount: parseFloat(c.amount),
          pos_definition_id: c.pos_definition_id,
          installment_count: parseInt(c.installment_count, 10) || 1,
          card_type: c.card_type || 'credit',
        })),
      transfers: transfers
        .filter(c => (parseFloat(c.amount) || 0) > 0.01)
        .map(c => ({ amount: parseFloat(c.amount), bank_account_id: c.bank_account_id })),
      vouchers: vouchers
        .filter(c => (parseFloat(c.amount) || 0) > 0.01 && c.voucher_id)
        .map(c => ({ amount: parseFloat(c.amount), shopping_voucher_id: c.voucher_id })),
      on_account: Math.max(0, onAccount),
    });
  };

  // ── Satır görselleri için ortak stiller ──
  const rowBase = (active) => ({
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 10px',
    background: active ? 'var(--accent-glow)' : 'var(--bg-secondary)',
    border: '2px solid ' + (active ? 'var(--accent-primary)' : 'var(--border-secondary)'),
    borderRadius: 8, cursor: 'pointer',
  });
  const amtInput = {
    width: 130, padding: '8px 10px', fontSize: '17px',
    border: '1px solid var(--border-input)', borderRadius: 6,
    fontFamily: 'monospace', fontWeight: 800, textAlign: 'right',
    background: 'var(--bg-card)',
  };
  const bankSel = (warn) => ({
    flex: 1, minWidth: 0, padding: '7px 8px', fontSize: 12,
    border: '1px solid ' + (warn ? '#dc2626' : 'var(--border-input)'),
    borderRadius: 6, background: 'var(--bg-card)',
  });
  const xBtn = {
    background: '#fee2e2', color: '#dc2626', border: 'none',
    borderRadius: 6, width: 28, height: 28, cursor: 'pointer',
    fontSize: 16, fontWeight: 800, lineHeight: 1,
  };
  const addBtn = (color) => ({
    padding: '6px 10px', background: 'transparent',
    border: '1px dashed ' + color, color: color,
    borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 12, width: 'min(820px, 96vw)',
        maxHeight: '95vh', overflowY: 'auto',
        padding: 16, display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '1.25rem', fontWeight: 800 }}>💳 Bölmeli Ödeme</span>
          <button onClick={onClose} title="Kapat"
            style={{ marginLeft: 'auto', background: 'transparent', border: '1px solid var(--border-secondary)', fontSize: '20px', width: 36, height: 36, borderRadius: 8, cursor: 'pointer' }}>×</button>
        </div>

        {/* Toplam */}
        <div style={{
          textAlign: 'center', padding: '10px 16px',
          background: 'var(--accent-primary)', color: '#fff', borderRadius: 8,
        }}>
          <div style={{ fontSize: '11px', opacity: 0.9 }}>ÖDENECEK TUTAR</div>
          <div style={{ fontSize: '1.9rem', fontFamily: 'JetBrains Mono, monospace', fontWeight: 800 }}>{fmt(total)} ₺</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Nakit (tekil) */}
          <div onClick={() => setActiveRow('cash')} style={rowBase(activeRow === 'cash')}>
            <span style={{ fontSize: 20 }}>💵</span>
            <span style={{ fontWeight: 700, flex: 1, fontSize: '14px' }}>
              Nakit
              {!hasCashRegister && <span style={{ marginLeft: 6, color: '#dc2626', fontSize: 11 }}>⚠ Kasa yok!</span>}
            </span>
            <input type="text" inputMode="decimal" value={cash}
              onClick={(e) => { e.stopPropagation(); setActiveRow('cash'); }}
              onChange={(e) => setCash(e.target.value)}
              style={amtInput} />
            <span style={{ fontWeight: 700, color: 'var(--text-tertiary)', minWidth: 14 }}>₺</span>
          </div>

          {/* Kredi Kartı satırları (N adet) — POS + Taksit + Tutar */}
          {cards.map((c) => {
            const active = activeRow === 'card_' + c.id;
            return (
              <div key={c.id} onClick={() => setActiveRow('card_' + c.id)} style={rowBase(active)}>
                <span style={{ fontSize: 20 }}>💳</span>
                <span style={{ fontWeight: 700, fontSize: '12px', minWidth: 46 }}>K.Kart</span>
                <select value={c.pos_definition_id}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setCardField(c.id, 'pos_definition_id', e.target.value)}
                  style={{ ...bankSel(!c.pos_definition_id), flex: 1.4 }}>
                  <option value="">— POS seç —</option>
                  {posDefinitions.map(p => (
                    <option key={p.id} value={p.id}>{(p.bank_name || '-') + ' / ' + (p.pos_name || '-')}</option>
                  ))}
                </select>
                <select value={c.installment_count}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setCardField(c.id, 'installment_count', e.target.value)}
                  title="Taksit sayısı"
                  style={{ ...bankSel(false), flex: 0, minWidth: 72, width: 72 }}>
                  {INSTALLMENT_OPTIONS.map(n => (
                    <option key={n} value={n}>{n === 1 ? 'Peşin' : (n + ' tx')}</option>
                  ))}
                </select>
                <input type="text" inputMode="decimal" value={c.amount}
                  onClick={(e) => { e.stopPropagation(); setActiveRow('card_' + c.id); }}
                  onChange={(e) => setCardField(c.id, 'amount', e.target.value)}
                  style={amtInput} />
                <span style={{ fontWeight: 700, color: 'var(--text-tertiary)', minWidth: 14 }}>₺</span>
                <button onClick={(e) => { e.stopPropagation(); removeCard(c.id); }} style={xBtn} title="Bu satırı sil">×</button>
              </div>
            );
          })}

          {/* Banka Havale satırları (N adet) */}
          {transfers.map((c) => {
            const active = activeRow === 'transfer_' + c.id;
            return (
              <div key={c.id} onClick={() => setActiveRow('transfer_' + c.id)} style={rowBase(active)}>
                <span style={{ fontSize: 20 }}>🏦</span>
                <span style={{ fontWeight: 700, fontSize: '13px', minWidth: 70 }}>Havale</span>
                <select value={c.bank_account_id}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setTransferField(c.id, 'bank_account_id', e.target.value)}
                  style={bankSel(!c.bank_account_id)}>
                  <option value="">— Banka seç —</option>
                  {bankAccounts.map(b => (
                    <option key={b.id} value={b.id}>{b.title || b.name || b.bank_name || b.account_no}</option>
                  ))}
                </select>
                <input type="text" inputMode="decimal" value={c.amount}
                  onClick={(e) => { e.stopPropagation(); setActiveRow('transfer_' + c.id); }}
                  onChange={(e) => setTransferField(c.id, 'amount', e.target.value)}
                  style={amtInput} />
                <span style={{ fontWeight: 700, color: 'var(--text-tertiary)', minWidth: 14 }}>₺</span>
                <button onClick={(e) => { e.stopPropagation(); removeTransfer(c.id); }} style={xBtn} title="Bu satırı sil">×</button>
              </div>
            );
          })}

          {/* Voucher satırları (N adet) — müşterinin issued çeklerinden seçim */}
          {vouchers.map((c) => {
            const active = activeRow === 'voucher_' + c.id;
            // Bu satıra şu an seçili olan voucher + diğer satırlarca kapılmamış olanlar gösterilsin
            const visible = availableVouchers.filter(v => v.id === c.voucher_id || !selectedVoucherIds.has(v.id));
            const cur = availableVouchers.find(v => v.id === c.voucher_id);
            return (
              <div key={c.id} onClick={() => setActiveRow('voucher_' + c.id)} style={rowBase(active)}>
                <span style={{ fontSize: 20 }}>🎫</span>
                <span style={{ fontWeight: 700, fontSize: '12px', minWidth: 46 }}>Çek</span>
                <select value={c.voucher_id}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setVoucherField(c.id, 'voucher_id', e.target.value)}
                  style={{ ...bankSel(!c.voucher_id), flex: 1.6 }}>
                  <option value="">— Çek seç —</option>
                  {visible.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.voucher_no} ({fmt(v.amount)} ₺{v.expiry_date ? ' · son ' + v.expiry_date.slice(0, 10) : ''})
                    </option>
                  ))}
                </select>
                <input type="text" inputMode="decimal" value={c.amount}
                  onClick={(e) => { e.stopPropagation(); setActiveRow('voucher_' + c.id); }}
                  onChange={(e) => setVoucherField(c.id, 'amount', e.target.value)}
                  title={cur ? `Çek tutarı: ${fmt(cur.amount)} ₺` : ''}
                  style={amtInput} />
                <span style={{ fontWeight: 700, color: 'var(--text-tertiary)', minWidth: 14 }}>₺</span>
                <button onClick={(e) => { e.stopPropagation(); removeVoucher(c.id); }} style={xBtn} title="Bu satırı sil">×</button>
              </div>
            );
          })}

          {/* Satır Ekle butonları */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
            {allowCard && (
              <button onClick={addCard} style={addBtn('var(--accent-primary)')} disabled={posDefinitions.length === 0}
                title={posDefinitions.length === 0 ? 'POS tanımı yok' : ''}>＋ Kart Ekle (Çoklu POS)</button>
            )}
            {allowTransfer && (
              <button onClick={addTransfer} style={addBtn('var(--accent-primary)')} disabled={bankAccounts.length === 0}
                title={bankAccounts.length === 0 ? 'Banka hesabı yok' : ''}>＋ Havale Ekle (Banka)</button>
            )}
            {allowVoucher && (
              <button onClick={addVoucher} style={addBtn('var(--accent-primary)')}
                disabled={availableVouchers.length === 0 || vouchers.length >= availableVouchers.length}
                title={availableVouchers.length === 0 ? 'Müşterinin kullanılabilir çeki yok' : ''}>
                ＋ Çek Ekle ({availableVouchers.length} kullanılabilir)
              </button>
            )}
            {allowCard && posDefinitions.length === 0 && <span style={{ fontSize: 11, color: '#dc2626', alignSelf: 'center' }}>⚠ POS tanımı yok — Kart eklenemez</span>}
            {allowTransfer && bankAccounts.length === 0 && <span style={{ fontSize: 11, color: '#dc2626', alignSelf: 'center' }}>⚠ Banka hesabı yok — Havale eklenemez</span>}
            {(!allowCard && !allowTransfer && !allowVoucher) && (
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)', alignSelf: 'center' }}>
                Sadece Nakit{allowOnAcct ? ' / Cari' : ''} ödeme alabilirsiniz (yetki kısıtı).
              </span>
            )}
          </div>

          {/* Cari'ye kalan satırı (otomatik) — yetki yoksa kırmızı uyarı */}
          {(() => {
            const onAcctBlocked = onAccount > 0.01 && !allowOnAcct;
            if (onAcctBlocked) {
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', marginTop: 4,
                  background: 'rgba(239,68,68,0.10)',
                  border: '2px solid var(--danger)', borderRadius: 8,
                }}>
                  <span style={{ fontSize: 22 }}>🚫</span>
                  <span style={{ fontWeight: 700, flex: 1, fontSize: '14px', color: 'var(--danger)' }}>
                    Eksik Tutar — Cariye Yazma Yetkiniz Yok
                    <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--danger)', marginTop: 2 }}>
                      Tam ödeme alın veya yöneticinizden cari açık hesap yetkisi isteyin
                    </div>
                  </span>
                  <span style={{
                    padding: '10px 12px', fontSize: '18px',
                    fontFamily: 'monospace', fontWeight: 800, textAlign: 'right', minWidth: 140,
                    color: 'var(--danger)',
                  }}>{fmt(Math.abs(onAccount))} ₺</span>
                </div>
              );
            }
            if (onAccount <= 0.01 && onAccount >= -0.01) return null; // tam ödeme — boş satır gerekmez
            return (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', marginTop: 4,
                background: onAccount > 0.01 ? 'var(--warning-bg)' : 'var(--success-bg)',
                border: '2px solid ' + (onAccount > 0.01 ? 'var(--warning)' : 'var(--success)'),
                borderRadius: 8,
              }}>
                <span style={{ fontSize: 22 }}>📒</span>
                <span style={{ fontWeight: 700, flex: 1, fontSize: '14px' }}>
                  {onAccount > 0.01 ? 'Cariye Yazılacak (Borç)' : 'Para Üstü (Müşteriye)'}
                  {customer ? <span style={{ marginLeft: 6, color: 'var(--text-tertiary)', fontSize: 12, fontWeight: 500 }}>· {customer.title}</span> : null}
                </span>
                <span style={{
                  padding: '10px 12px', fontSize: '18px',
                  fontFamily: 'monospace', fontWeight: 800, textAlign: 'right', minWidth: 140,
                  color: onAccount > 0.01 ? 'var(--warning)' : 'var(--success)',
                }}>{fmt(Math.abs(onAccount))} ₺</span>
              </div>
            );
          })()}
        </div>

        {/* Hızlı doldur */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setActiveValue(total.toFixed(2))} style={presetBtn}>Bu satıra Tam</button>
          <button onClick={() => { setCash(total.toFixed(2)); setCards([]); setTransfers([]); setVouchers([]); setActiveRow('cash'); }} style={presetBtn}>Hepsi Nakit</button>
          {allowCard && (
            <button onClick={() => { setCash('0'); setTransfers([]); setVouchers([]); const id = uid(); setCards([{ id, amount: total.toFixed(2), pos_definition_id: defaultPosId, installment_count: 1, card_type: 'credit' }]); setActiveRow('card_' + id); }} style={presetBtn} disabled={posDefinitions.length === 0}>Hepsi Kart</button>
          )}
          {allowOnAcct && (
            <button onClick={() => { setCash('0'); setCards([]); setTransfers([]); setVouchers([]); setActiveRow('cash'); }} style={presetBtn}>Hepsi Cariye</button>
          )}
        </div>

        {/* Numerik pad */}
        <Keypad
          compact
          onPress={numPress}
          keys={[
            { k: '1' }, { k: '2' }, { k: '3' },
            { k: 'sil', label: '⌫\nSil', color: '#ef4444', textColor: '#fff', action: () => numPress('←') },
            { k: '4' }, { k: '5' }, { k: '6' },
            { k: 'clr', label: '✕\nTemizle', color: '#dc2626', textColor: '#fff', action: () => numPress('C') },
            { k: '7' }, { k: '8' }, { k: '9' },
            { k: 'pay', label: '✓\nÖde', color: 'var(--success)', textColor: '#fff', action: handleComplete },
            { k: '0' }, { k: '00' }, { k: '.' },
          ]}
        />

        {/* TAMAMLA */}
        <button
          onClick={handleComplete}
          disabled={saving || insufficient || (onAccount > 0.01 && !allowOnAcct)}
          style={{
            padding: '16px',
            background: (onAccount > 0.01 && !allowOnAcct) ? 'var(--danger)' : 'var(--success)',
            color: '#fff',
            border: 'none', borderRadius: 8, fontSize: '17px', fontWeight: 800,
            cursor: (saving || (onAccount > 0.01 && !allowOnAcct)) ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.5 : ((onAccount > 0.01 && !allowOnAcct) ? 0.85 : 1),
          }}>
          {saving ? 'KAYDEDİLİYOR...'
            : (onAccount > 0.01 && !allowOnAcct) ? `🚫 EKSİK TUTAR (₺${fmt(onAccount)}) — TAM ÖDEME ALIN`
            : `✓ SATIŞI TAMAMLA${onAccount > 0.01 ? ' (₺' + fmt(onAccount) + ' cariye)' : ''}${overpaid > 0.01 ? ' (₺' + fmt(overpaid) + ' para üstü)' : ''}`}
        </button>
      </div>
    </div>
  );
}

// ── Ortak Keypad bileşeni — tüm POS modallarında aynı görünüm ────
// keys: [{ k, label?, color?, textColor?, action?, span? }]
function Keypad({ keys, onPress, compact = false }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
      {keys.map((b, i) => {
        const isAction = !!b.action;
        const handler = isAction ? b.action : () => onPress(b.k);
        return (
          <button key={i} onClick={handler}
            style={{
              background: b.color || 'var(--bg-secondary)',
              color: b.textColor || 'var(--text-primary)',
              border: '1px solid var(--border-secondary)',
              borderRadius: 6, fontSize: isAction ? (compact ? '11px' : '12px') : (compact ? '17px' : '20px'),
              fontWeight: 700, cursor: 'pointer',
              fontFamily: isAction ? 'inherit' : 'JetBrains Mono, monospace',
              minHeight: compact ? 48 : 60, whiteSpace: 'pre-line',
              gridColumn: b.span ? 'span ' + b.span : undefined,
              transition: 'transform 0.05s',
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >{b.label || b.k}</button>
        );
      })}
    </div>
  );
}

const presetBtn = {
  padding: '8px 14px', background: 'var(--bg-secondary)',
  border: '1px solid var(--border-secondary)', borderRadius: 6,
  fontSize: '13px', fontWeight: 700, cursor: 'pointer',
  fontFamily: 'monospace',
};

const newCustLabel = {
  display: 'block', fontSize: '11px', color: 'var(--text-secondary)',
  marginBottom: 3, fontWeight: 600,
};

const newCustInput = {
  width: '100%', padding: '8px 10px', fontSize: '13px',
  border: '1px solid var(--border-secondary)', borderRadius: 6, outline: 'none',
  background: 'var(--bg-primary)', color: 'var(--text-primary)',
};
